/**
 * D-058: Riser Documentation (riser dosyası).
 *
 * Bir riserin belgeleri Oil States onarım dosyasının (overhaul record book, şablon OSIUK-Q-XX-0001 Rev 5) 5 bölümü ve 30 kalemiyle
 * aynı sırada gösterilir. Bu dosya o sayfanın sözlüğü ve kurallarıdır: bölümler ve kalemler, belge türü adları, belgenin kaleme
 * sınıflanması, kalem durumu ve sayaçlar, yanıt tipleri. Saf: veri erişimi yok; sunucu (okuma servisi, Excel) ve web aynı kuralı okur.
 *
 * Sunucu ekrana hazır cümle göndermez: tür anahtarı, kapsam, eksik yuvalar ve sayılar yapılandırılmış veridir; metni web kurar.
 * İstisna: bölüm ve kalem adları (şablondaki adlar, çevrilmez), belge türü adları, durum adları ve ekran adları burada İngilizce
 * sabittir; web bunları `cevir(degisken)` ile gösterir.
 *
 * Akış: sunucu her belgeyi kendi bağ alanından okuyup düz `DosyaSatiri` listesi, beyanlar ve riserin olguları (`DosyaOlgulari`)
 * olarak verir; `dosyaKur` bölümleri, 30 kalemlik listeyi, sayaçları ve belge sayısını kurar. Riser seçicideki sayı ile riserin
 * sayfasındaki sayı aynı fonksiyondan çıkar.
 *
 * D-059: Project documents (bölüm 5 ve 6). Riser dosyasının kendi ekranı olmayan belgeleri ve bağları o ekrandan girilir: proje,
 * iş emri ve riser belgeleri, hattın ya da parçanın geldiği riser, OEM kitabı sayfa düzeltmeleri, rapordaki test ekipmanı ve
 * imzalayan. Bu dosya o ekranın da sözleşmesi (okuma yanıtları, yazma gövdeleri) ve saf denetimleridir. Denetimler İngilizce hata
 * cümlesi ya da null döner: sunucu cümleyi olduğu gibi yazar, web sözlükten çevirir.
 *
 * D-062 / D-063: NDT inspektörleri Registers'ta adlarıyla tutulur (kütük "ndt_inspektor"; sertifikaları `kutuk_belge`'de). Raporun
 * imzalayanı `dokuman.iz_baglari.ndtInspektor` listesinde inspektörün KİMLİĞİYLE durur; ad yalnız gösterimdir. 5.4 kütük üzerinden
 * dolar (5.5 test ekipmanı yolunun eşi). D-059'un kodlu çözümü kalktı: "ndt_personel" kapsamı, "ndt_yeterlilik" girdi türü,
 * "NDT personnel" sekmesi ve personel kodu kuralları bu dosyada yoktur. Eski satırlar veritabanında durur, okunmaz; bu iki değer
 * adı başka anlamla yeniden kullanılmaz.
 */
import { yoneticiMi } from "./giris.js";
import { BORU_BAGLANTI, BOX_BAGLANTI, PIN_BAGLANTI } from "./kesim-plani.js";
import { FITUP_HAZIRLIK, FITUP_ONAY, hidrotestAdimiMi, kaynakAdimiMi, pwhtAdimiMi, testAdimiMi } from "./rota.js";
import { NDT_AILE, NDT_KAYNAK_EKRAN, SET_HATLARI, setHatMi } from "./set.js";
import { riserRnBicimi } from "./stok-paket.js";
import { yetkiliMi } from "./yetki.js";
/* ====================================================================================================================
 * 1 · Sözlük: bölümler, kalemler, kalem grupları, durumlar
 * ================================================================================================================== */
/** Oil States dosyasının beş bölümü (adlar şablondaki gibi; çevrilmez). */
export const DOSYA_BOLUMLERI = [
    { no: "1", ad: "Finished Product Documentation" },
    { no: "2", ad: "Certifying Authority Documentation" },
    { no: "3", ad: "Assembly Traceability and Manufacturing" },
    { no: "4", ad: "Component Traceability and Manufacturing" },
    { no: "5", ad: "Supplementary Documentation" },
];
/** Şablonun 30 kalemi (adlar "Recertification document list" tablosuyla birebir; çevrilmez). */
export const DOSYA_KALEMLERI = [
    { no: "1.1", bolum: "1", ad: "OSI Certificate of Conformance" },
    { no: "1.2", bolum: "1", ad: "Purchase Contract" },
    { no: "1.3", bolum: "1", ad: "Bill of Materials" },
    { no: "1.4", bolum: "1", ad: "General Arrangement Drawing" },
    { no: "1.5", bolum: "1", ad: "Assembly Identification" },
    { no: "2.1", bolum: "2", ad: "Design Approval" },
    { no: "2.2", bolum: "2", ad: "Certificate / Report" },
    { no: "3.1", bolum: "3", ad: "Material Test Records" },
    { no: "3.2", bolum: "3", ad: "Inspection Survey Report" },
    { no: "3.3", bolum: "3", ad: "Inspection & Test Plan / Quality Plan" },
    { no: "3.4", bolum: "3", ad: "Assembly Traceability Record" },
    { no: "3.5", bolum: "3", ad: "Factory Acceptance Test Records" },
    { no: "3.6", bolum: "3", ad: "Assembly Coating Records" },
    { no: "3.7", bolum: "3", ad: "Coating Certificate of Compliance" },
    { no: "3.8", bolum: "3", ad: "Preservation Record" },
    { no: "3.9", bolum: "3", ad: "As-built Dimensional Record" },
    { no: "4.1", bolum: "4", ad: "Material Test Records" },
    { no: "4.2", bolum: "4", ad: "Pre-weld Non-destructive Testing Records" },
    { no: "4.3", bolum: "4", ad: "Welding Records" },
    { no: "4.4", bolum: "4", ad: "Post-weld Heat Treatment Records" },
    { no: "4.5", bolum: "4", ad: "Non-destructive Testing Records" },
    { no: "4.6", bolum: "4", ad: "Dimensional Inspection Records" },
    { no: "4.7", bolum: "4", ad: "Drift Test Records" },
    { no: "4.8", bolum: "4", ad: "Coating Records" },
    { no: "5.1", bolum: "5", ad: "Weld Procedure Specifications" },
    { no: "5.2", bolum: "5", ad: "Weld Consumable Certificates" },
    { no: "5.3", bolum: "5", ad: "Welder and Welding Operator Qualifications" },
    { no: "5.4", bolum: "5", ad: "Non-destructive Testing Technician Qualifications" },
    { no: "5.5", bolum: "5", ad: "Calibration Records" },
    { no: "5.6", bolum: "5", ad: "Miscellaneous" },
];
const KALEM_SIRASI = new Map(DOSYA_KALEMLERI.map((k, i) => [k.no, i]));
const KALEM_TANIMI = new Map(DOSYA_KALEMLERI.map((k) => [k.no, k]));
/** Kalemin tanımı (no, bölüm, ad). */
export const kalemTanimi = (no) => KALEM_TANIMI.get(no);
const bolumu = (no) => kalemTanimi(no).bolum;
/**
 * "Documents on file" sekmesinde kalem başlıklarının SABİT gruplaması (boşluğa göre değişmez). Bölüm 4'te grup yoktur: iş blokları
 * ve "Used on these welds — from the Registers" satırları çizilir; 5.1, 5.2, 5.3 belgeleri de o satırlardadır.
 */
export const DOSYA_KALEM_GRUPLARI = [
    { bolum: "1", kalemler: ["1.1"] },
    { bolum: "1", kalemler: ["1.2"] },
    { bolum: "1", kalemler: ["1.3"] },
    { bolum: "1", kalemler: ["1.4"] },
    { bolum: "1", kalemler: ["1.5"] },
    { bolum: "2", kalemler: ["2.1", "2.2"], not: "imzali_dosya_sirasi" },
    { bolum: "3", kalemler: ["3.1"] },
    { bolum: "3", kalemler: ["3.2"] },
    { bolum: "3", kalemler: ["3.3"] },
    { bolum: "3", kalemler: ["3.4"] },
    { bolum: "3", kalemler: ["3.5"] },
    { bolum: "3", kalemler: ["3.6"] },
    { bolum: "3", kalemler: ["3.7", "3.8", "3.9"] },
    { bolum: "5", kalemler: ["5.1", "5.2", "5.3"], not: "bolum_4" },
    { bolum: "5", kalemler: ["5.4"] },
    { bolum: "5", kalemler: ["5.5"] },
    { bolum: "5", kalemler: ["5.6"] },
];
/** Kalem durumu; sıra ekrandaki sayaç çiplerinin sırasıdır. */
export const KALEM_DURUMLARI = ["dahil", "beyan", "sonda", "eksik", "dogmaz"];
/** Durumun ekran ve Excel adı. */
export const KALEM_DURUMU_ADI = {
    dahil: "Included",
    beyan: "No record declared",
    sonda: "Issued at the end",
    eksik: "Missing",
    dogmaz: "Not applicable",
};
/* ====================================================================================================================
 * 1 · Sözlük: belge türleri, ekran adları, girdi türleri
 * ================================================================================================================== */
/**
 * Belge türü adları (statik sayfadaki satır adlarıyla birebir). "{0}" ara değeri web doldurur (verici riserin RN'i).
 * Kütük belgelerinde ad tür ailesidir ("WPS and PQR"); belgenin kendi türü (WPS / PQR, WPQR / Continuity) satırın alt metnindedir.
 */
export const DOSYA_TUR_ADI = {
    // Bölüm 1
    osi_coc: "Certificate of Conformity",
    musteri_siparisi: "Customer purchase order",
    bom: "Bill of Materials",
    montaj_resmi: "General Arrangement Drawing",
    kimlik_plakasi: "Identification plate photo",
    // Bölüm 2
    dnv_urun_sertifikasi: "DNV Product Certificate (original manufacture)",
    dvr_oem: "DNV Design Verification Report — OEM design",
    dvr_osi: "DNV Design Verification Report — Oil States design",
    dnv_survey_report: "DNV Survey Report",
    // Bölüm 3
    malzeme_sertifika_listesi: "Material certification list (OSIUK-Q-FM-0024)",
    muayene_raporu: "Conventional Inspection report (with disposition)",
    rework_raporu: "Rework Scope report",
    verici_muayene_raporu: "Conventional Inspection report of riser {0}",
    hat_ndt: "NDT report",
    // Kaynağı olmayan işin test raporu (ör. Dress-out işinin "Re-NDT"si): ad nötrdür, adım adı satırda ayrıca yazılır (`kaynaksizIsTuru`).
    is_ndt: "NDT report",
    basinc_sonrasi_dpi: "Penetrant testing report after pressure test",
    kalite_plani: "Quality inspection and test plan",
    fm0011: "Disassembly Traceability Record (FM-0011), signed",
    fm0012: "Assembly Traceability Record (FM-0012)",
    basinc_testi: "Pressure Test Certificate and chart",
    boya_raporu: "Coating report",
    boya_uygunluk: "Coating Certificate of Compliance",
    // Bölüm 4
    malzeme_sertifikasi: "Material certificate EN 10204 3.1",
    uygunluk_sertifikasi: "Certificate of conformity (CoC)",
    bevel_ndt: "Bevel NDT report (before welding)",
    fitup_kaydi: "Fit-up record",
    weld_map: "Weld map",
    pwht_kaydi: "PWHT record",
    kaynak_sonrasi_ndt: "NDT report after welding",
    pwht_sonrasi_ndt: "NDT report after PWHT",
    // Bölüm 5
    wps_pqr: "WPS and PQR",
    sarf_sertifikasi: "Consumable certificate EN 10204 3.1",
    kaynakci_yeterlilik: "Welder qualification",
    kaynakci_sureklilik: "Continuity / prolongation record",
    ndt_yeterlilik: "NDT technician qualification record",
    kalibrasyon_test: "Calibration certificates — pressure test equipment",
    kalibrasyon_fitup: "Calibration certificates — fit-up instruments",
    prosedur: "Procedure",
    beyan: "Declarations in place of documents",
    // Reference
    mrb: "OEM manufacturing record book",
    onceki_cevrim: "Previous cycle documents",
    foto_calisma: "Photos and working files",
    adim_belgesi: "Step document",
    satin_alma_siparisi: "PO",
    irsaliye: "Delivery note / packing list",
    // D-074 Faz 3 (K20): kütükte yeni belge türleri; kalemleri yok, Reference'ta durur.
    ticari_fatura: "Commercial invoice",
    sarf_kabul_formu: "Consumable receiving inspection form",
    ce_uygunluk_beyani: "CE attestation of conformity",
    uretici_test_raporu: "Manufacturer test report",
    dolgu_onayi: "Alternative filler metal approval",
};
/** `dokuman.kaynak_ekran` değeri → ekran adı. Finishing iki değerle yazar (HT raporu "planlama", ötekiler "assembly"). */
export const KAYNAK_EKRAN_ADI = {
    mrb: "MRB intake",
    teslim: "Receiving",
    sokum: "Disassembly",
    saha: "Field list",
    muayene: "Inspection verification",
    komponent: "Component",
    fitup: "Fit-up",
    weldmap: "Weld map",
    assembly: "Finishing",
    planlama: "Finishing",
    kutukler: "Registers",
    "proje-belgeleri": "Project documents",
};
/**
 * D-059: Project documents ekranından yüklenen her dosyanın `dokuman.aile` değeri (riser_rn ve hat boş). Tür ve kapsam dosyada değil
 * bağ satırındadır (`dosya_belge`): aynı PDF iki kapsama bağlanınca tek dosya satırı kalır.
 */
export const DOSYA_BELGE_AILESI = "Project document";
/** D-059: o yüklemelerin `dokuman.kaynak_ekran` değeri; ekran adı `KAYNAK_EKRAN_ADI`'ndadır ("Project documents"). */
export const DOSYA_BELGE_KAYNAK_EKRAN = "proje-belgeleri";
/** Kütük belgesinin kaynak ekranı ("via Register" satırlarının son kolonu). */
export const KUTUK_EKRAN_ADI = {
    stok_paket: "Registers › Stock",
    wps: "Registers › WPS",
    kaynakci: "Registers › Welders",
    ndt_inspektor: "Registers › NDT inspectors",
    sarf: "Registers › Consumable lots",
    alet: "Registers › Instruments",
};
/** Hat adları (izlenebilirlik tablosu ve Bölüm 4 blok başlıkları). */
export const DOSYA_HAT_ADI = { Main: "Main Tube", Choke: "Choke", Kill: "Kill", Booster: "Booster", Hydraulic: "Hydraulic" };
/** Basınç testi beklenen hatlar (3.5); ana boruda basınç testi yoktur. */
export const BASINC_TESTI_HATLARI = ["Choke", "Kill", "Booster", "Hydraulic"];
/** Belgeyi kimin açabileceğini belirleyen sınıf (`belgeErisimi`). */
export const BELGE_SINIFLARI = ["acik", "kutuk", "kisisel", "fiyat"];
/**
 * Ek girdinin (kendi adımının ekranı olmayan belge) kapsamı. D-063: "ndt_personel" kapsamı kalktı; `dosya_belge`'deki eski satırlar
 * yerinde durur ve okunmaz (kapsam sözlükte olmadığı için atlanır).
 */
export const DOSYA_GIRDI_KAPSAMLARI = ["proje", "is_emri", "riser"];
/**
 * Ek girdi belge türleri: türün kalemi, izinli kapsamları, tekil mi (aynı kapsamda tek etkin belge) ve erişim sınıfı.
 * D-059: bu belgeler Project documents ekranından girilir (`dosya_belge`). Ekran bir türü yalnız sekmesinin kapsamlarıyla sunar
 * (`girdiKapsamlari`); buradaki kapsam listesi okuma tarafının sözlüğüdür.
 * D-063: "ndt_yeterlilik" girdi türü kalktı (5.4 belgeleri Registers › NDT inspectors'tan gelir: `kutukBelgeTuru`); kişisel sınıfta
 * girdi türü kalmadı.
 */
export const DOSYA_GIRDI_TURLERI = {
    osi_coc: { kalem: "1.1", kapsamlar: ["riser"], tekil: true, sinif: "acik" },
    musteri_siparisi: { kalem: "1.2", kapsamlar: ["proje", "is_emri"], tekil: true, sinif: "fiyat" },
    bom: { kalem: "1.3", kapsamlar: ["proje", "is_emri"], tekil: false, sinif: "acik" },
    montaj_resmi: { kalem: "1.4", kapsamlar: ["proje", "is_emri"], tekil: false, sinif: "acik" },
    kimlik_plakasi: { kalem: "1.5", kapsamlar: ["riser"], tekil: false, sinif: "acik" },
    dvr_osi: { kalem: "2.1", kapsamlar: ["proje", "is_emri"], tekil: false, sinif: "acik" },
    dnv_survey_report: { kalem: "2.2", kapsamlar: ["riser"], tekil: true, sinif: "acik" },
    malzeme_sertifika_listesi: { kalem: "3.1", kapsamlar: ["riser"], tekil: false, sinif: "acik" },
    kalite_plani: { kalem: "3.3", kapsamlar: ["proje", "is_emri", "riser"], tekil: false, sinif: "acik" },
    prosedur: { kalem: "5.6", kapsamlar: ["proje", "is_emri"], tekil: false, sinif: "acik" },
    onceki_cevrim: { kalem: "ref", kapsamlar: ["riser"], tekil: false, sinif: "acik" },
};
/** Belge yerine yapılan beyanlar. */
export const BEYAN_TURLERI = ["kayit_yok", "foto_yok", "olcum_yok"];
/** Beyanın ekran adı (var olan ekranlardaki metinlerle aynı). */
export const BEYAN_ADI = {
    kayit_yok: "No record available",
    foto_yok: "No photo available",
    olcum_yok: "No measurement record available",
};
/* ====================================================================================================================
 * 2 · Sınıflama: aile, adım adı, beyan, kütük belgesi
 * ================================================================================================================== */
const CALISMA = { kalem: "ref", tur: "foto_calisma" };
/**
 * `dokuman.aile` → kalem ve belge türü. Var olan aile değerleri yeniden adlandırılmaz, yalnız eşlenir.
 * Kütük belgelerinin aileleri burada "ref" ve türsüzdür: onlar `kutuk_belge` bağından `kutukBelgeKalemi` ile sınıflanır.
 */
const AILE_SOZLUGU = {
    MRB: { kalem: "ref", tur: "mrb" },
    "MRB-icerik": CALISMA,
    "Inspection-input": CALISMA,
    "Rework-gecis": CALISMA,
    "FM-0011-legacy-excel": CALISMA,
    "FM-0012-excel": CALISMA,
    "FM-0012-input": CALISMA,
    "Receiving-photo": CALISMA,
    "Receiving-damage-photo": CALISMA,
    "FM-0011-photo": CALISMA,
    "Repair-photo": CALISMA,
    "Fit-up-photo": CALISMA,
    "Weld-photo": CALISMA,
    "Step-add": { kalem: "ref", tur: "adim_belgesi" },
    SURVREP: { kalem: "3.2", tur: "muayene_raporu" },
    Disposition: { kalem: "3.2", tur: "muayene_raporu" },
    "SURVREP-rework": { kalem: "3.2", tur: "rework_raporu" },
    "FM-0011-signed": { kalem: "3.4", tur: "fm0011" },
    "FM-0011-legacy": { kalem: "3.4", tur: "fm0011" },
    "FM-0012": { kalem: "3.4", tur: "fm0012" },
    Hydrotest: { kalem: "3.5", tur: "basinc_testi" },
    "Coating-GOE": { kalem: "3.6", tur: "boya_raporu" },
    "Weld map": { kalem: "4.3", tur: "weld_map" },
    "EN10204-3.1": { kalem: "ref", tur: null },
    "Register document": { kalem: "ref", tur: null },
    "Stock certificate": { kalem: "ref", tur: null },
    // D-059: Project documents yüklemesi; kalemi ve türü bağ satırından gelir (`girdiBelgeSatiri`).
    [DOSYA_BELGE_AILESI]: { kalem: "ref", tur: null },
};
function aileSinifi(aile, kaynakEkran) {
    const a = aile ?? "";
    // Hat NDT raporu yalnız Finishing yüklemesidir (aile + kaynak ekran birlikte); "NDT: MT (bevel)" gibi adım raporları ayrı ailedir.
    if (a === NDT_AILE)
        return kaynakEkran === NDT_KAYNAK_EKRAN ? { kalem: "3.2", tur: "hat_ndt" } : CALISMA;
    const s = AILE_SOZLUGU[a];
    if (s)
        return s;
    // Sözlükte olmayan aile: Component adım raporu ailesine adım adını yazar. Başka ekrandan gelen tanınmayan aile Reference'tadır.
    return kaynakEkran === "komponent" ? { kalem: adimKalemi(a), tur: adimTuru(a) } : CALISMA;
}
/** Belgenin ailesinden kalemi. Kütük belgeleri, tanınmayan aileler ve kalıba uymayan adım adları "ref". */
export const aileKalemi = (aile, kaynakEkran) => aileSinifi(aile, kaynakEkran).kalem;
/** Belgenin ailesinden belge türü; kütük belgesi ailelerinde null (türü `kutukBelgeTuru` verir). */
export const aileTuru = (aile, kaynakEkran) => aileSinifi(aile, kaynakEkran).tur;
const bevelAdimiMi = (ad) => testAdimiMi(ad) && /\(bevel\)/i.test(ad);
const postTestAdimiMi = (ad) => testAdimiMi(ad) && /post-test/i.test(ad);
/**
 * Adım raporunun kalemi, adım adından. Sınama sırası: bevel → PWHT → post-test → hidrotest → kalan test; kalıba uymayan adım "ref".
 * "NDT + Hardness (post-PWHT)" PWHT ile başlamadığı için 4.5'tir.
 */
export function adimKalemi(adimAd) {
    if (bevelAdimiMi(adimAd))
        return "4.2";
    if (pwhtAdimiMi(adimAd))
        return "4.4";
    if (postTestAdimiMi(adimAd))
        return "3.2";
    if (hidrotestAdimiMi(adimAd))
        return "3.5";
    if (testAdimiMi(adimAd))
        return "4.5";
    return "ref";
}
/** Adım raporunun belge türü (adım adı satırda ayrıca yazılır). */
export function adimTuru(adimAd) {
    if (bevelAdimiMi(adimAd))
        return "bevel_ndt";
    if (pwhtAdimiMi(adimAd))
        return "pwht_kaydi";
    if (postTestAdimiMi(adimAd))
        return "basinc_sonrasi_dpi";
    if (hidrotestAdimiMi(adimAd))
        return "basinc_testi";
    if (testAdimiMi(adimAd))
        return /post-PWHT/i.test(adimAd) ? "pwht_sonrasi_ndt" : "kaynak_sonrasi_ndt";
    return "adim_belgesi";
}
/**
 * Adımın "No record available" beyanının kalemi (belge eşlemesinden ayrı): test ve PWHT adımları belgeleriyle aynı kalemde;
 * fit-up ve kaynak adımları 4.3; öteki adımların beyanı yalnız 5.6'da listelenir.
 */
export function beyanKalemi(adimAd) {
    const k = adimKalemi(adimAd);
    if (k !== "ref")
        return k;
    if (adimAd === FITUP_HAZIRLIK || adimAd === FITUP_ONAY || kaynakAdimiMi(adimAd))
        return "4.3";
    return "5.6";
}
/**
 * Kaynağı olmayan işin Bölüm 4 belgesi blok açmaz: test raporu (4.2, 4.5) 3.2'de, malzeme sertifikası (4.1) 3.1'de listelenir,
 * ötekiler "ref". Yönlendirilen satır yalnız listelenir; kalemin yuvasını doldurmaz.
 */
export function kaynaksizIsKalemi(kalem) {
    if (kalem === "4.1")
        return "3.1";
    if (kalem === "4.2" || kalem === "4.5")
        return "3.2";
    return kalem !== "ref" && bolumu(kalem) === "4" ? "ref" : kalem;
}
/**
 * Kaynağı olmayan işin test raporunun türü. Adım adından gelen tür kaynağa göre adlanır ("before welding", "after welding",
 * "after PWHT"); kaynak yapılmamış işte bu ad yanlış olur: tür nötr "NDT report" olur. Öteki türler değişmez.
 */
export function kaynaksizIsTuru(tur) {
    return tur === "bevel_ndt" || tur === "kaynak_sonrasi_ndt" || tur === "pwht_sonrasi_ndt" ? "is_ndt" : tur;
}
/**
 * `kutuk_belge` üzerinden gelen belgenin kalemi (dokuman ailesi ne olursa olsun). Stok paketinin 3.1 / 3.2 / CoC belgesi paketin
 * kullanıldığı yere göre: fit-up ya da iş malzeme satırı → 4.1; triyaj ya da parça listesi → 3.1. PO, irsaliye ve fatura "ref".
 * D-062: NDT inspektörünün sertifikası 5.4 (raporu imzalayan inspektörün kütük kaydından).
 */
export function kutukBelgeKalemi(kutuk, tur, hedef) {
    if (kutuk === "stok_paket") {
        if (tur !== "en10204_31" && tur !== "en10204_32" && tur !== "coc")
            return "ref";
        if (hedef === "fitup" || hedef === "bom")
            return "4.1";
        return hedef === "triaj" || hedef === "parca" ? "3.1" : "ref";
    }
    if (kutuk === "wps")
        return tur === "wps" || tur === "pqr" ? "5.1" : "ref";
    if (kutuk === "sarf")
        return tur === "en10204_31" ? "5.2" : "ref";
    if (kutuk === "kaynakci")
        return tur === "wpq" || tur === "sureklilik" ? "5.3" : "ref";
    if (kutuk === "ndt_inspektor")
        return tur === "ndt_sertifika" ? "5.4" : "ref";
    if (kutuk === "alet")
        return tur === "kalibrasyon" ? "5.5" : "ref";
    return "ref";
}
/**
 * Kütük belgesinin dosya türü. Aletin kalibrasyon belgesi nereden bağlandıysa o adla: basınç testi sertifikasında adı geçen ekipman
 * (`basincTesti`) ya da fit-up'ta seçilen alet. D-062: NDT inspektörünün sertifikası 5.4 satırıdır ("ndt_yeterlilik").
 */
export function kutukBelgeTuru(kutuk, tur, s) {
    if (kutuk === "stok_paket") {
        const paket = {
            en10204_31: "malzeme_sertifikasi", coc: "uygunluk_sertifikasi", po: "satin_alma_siparisi", teslim: "irsaliye",
            // D-074 Faz 3 (K20): 3.2, 3.1'in yerine geçer (aynı dosya türü); fatura Reference'ta.
            en10204_32: "malzeme_sertifikasi", fatura: "ticari_fatura",
        };
        return paket[tur] ?? null;
    }
    if (kutuk === "wps")
        return tur === "wps" || tur === "pqr" ? "wps_pqr" : tur === "dolgu_onayi" ? "dolgu_onayi" : null;
    if (kutuk === "sarf") {
        return tur === "en10204_31" ? "sarf_sertifikasi" : tur === "po" ? "satin_alma_siparisi" : tur === "kabul_formu" ? "sarf_kabul_formu" : null;
    }
    if (kutuk === "kaynakci")
        return tur === "wpq" ? "kaynakci_yeterlilik" : tur === "sureklilik" ? "kaynakci_sureklilik" : null;
    if (kutuk === "ndt_inspektor")
        return tur === "ndt_sertifika" ? "ndt_yeterlilik" : null;
    if (kutuk === "alet") {
        if (tur === "kalibrasyon")
            return s?.basincTesti ? "kalibrasyon_test" : "kalibrasyon_fitup";
        // D-074 Faz 3 (K20): CE attestation ve üretici test raporu kalibrasyon sayılmaz; Reference'ta durur.
        return tur === "ce_uygunluk" ? "ce_uygunluk_beyani" : tur === "uretici_test" ? "uretici_test_raporu" : null;
    }
    return null;
}
/**
 * Kütük belgesinin erişim sınıfı: kaynakçı ve NDT inspektörü belgeleri kişisel (dosya adında kişi adı olabilir: ad hiçbir role
 * yazılmaz), PO ve fatura fiyat taşır, ötekiler kütük belgesi.
 */
export function kutukBelgeSinifi(kutuk, tur) {
    if (kutuk === "kaynakci" || kutuk === "ndt_inspektor")
        return "kisisel";
    return tur === "po" || tur === "fatura" ? "fiyat" : "kutuk";
}
/* ====================================================================================================================
 * 2 · İş emri
 * ================================================================================================================== */
/** Muayene raporu başlığındaki iş emri numarası: "… · OSIUK WO 123456A" → "123456A"; yoksa null. */
export function isEmriNo(metin) {
    const m = /\bWO\s*[:#]?\s*(\d{6}[A-Z]?)\b/i.exec(metin ?? "");
    return m ? m[1].toUpperCase() : null;
}
/** Riserin iş emri: muayene setinin müşteri siparişi metnindeki numara → yoksa batch'in iş emri alanı → yoksa null. */
export function riserIsEmri(musteriPo, batchWo) {
    return isEmriNo(musteriPo) ?? ((batchWo ?? "").trim() || null);
}
/** MRB iç dizininin sayfa metni: "7" → 7–7, "8-9" → 8–9; okunamayan değer null. */
export function mrbSayfaAraligi(metin) {
    const m = /^\s*(\d+)\s*(?:[-–]\s*(\d+))?\s*$/.exec(metin ?? "");
    if (!m)
        return null;
    const bas = Number(m[1]);
    const son = m[2] ? Number(m[2]) : bas;
    return bas >= 1 && son >= bas ? { sayfaBas: bas, sayfaSon: son } : null;
}
/** OEM kitabının hat bölümleri: 1 Main Tube · 2A, 2B Choke / Kill · 3 Hydraulic · 4 Booster. */
export const OEM_BOLUMLERI = ["1", "2A", "2B", "3", "4"];
/**
 * Hat bölümlerinin sayfa aralığı. Kural: ağacın derinlik 0 satırının kaynak sayfalarından ("p3·p30·p115·p117"; P kodu değil, sayfa)
 * iç dizinde sınıfı "DIVIDER" olan sayfaya denk gelen SON sayfa bölümün başıdır (bölüm 1'de iki ayraç eşleşir: sonuncusu);
 * bitiş sıradaki bölümün başından bir önceki sayfa, bölüm 4'ün bitişi dizinin en büyük sayfası.
 * Başı bulunamayan bölüm ve ardılının başı bulunamayan bölüm null (uydurulmaz). Hızlı vites kitapta (hiçbir satırda kapsam yok)
 * kural güvenilir değildir: hepsi null.
 */
export function oemBolumSayfalari(agac, pieceler) {
    const sonuc = { "1": null, "2A": null, "2B": null, "3": null, "4": null };
    if (!pieceler.some((p) => (p.kapsam ?? "").trim() !== ""))
        return sonuc;
    const ayraclar = new Set();
    let sonSayfa = 0;
    for (const p of pieceler) {
        const a = mrbSayfaAraligi(p.sayfa);
        if (!a)
            continue;
        sonSayfa = Math.max(sonSayfa, a.sayfaSon);
        if (p.sinif === "DIVIDER")
            ayraclar.add(a.sayfaBas);
    }
    const baslar = OEM_BOLUMLERI.map((b) => {
        const satir = agac.find((s) => s.derinlik === 0 && s.section.trim() === b);
        const eslesen = [...(satir?.kaynakPiece ?? "").matchAll(/\bp(\d+)/g)].map((m) => Number(m[1])).filter((n) => ayraclar.has(n));
        return eslesen.length ? eslesen[eslesen.length - 1] : null;
    });
    OEM_BOLUMLERI.forEach((b, i) => {
        const bas = baslar[i] ?? null;
        const sonraki = i < OEM_BOLUMLERI.length - 1 ? (baslar[i + 1] ?? null) : sonSayfa + 1;
        if (bas !== null && sonraki !== null && sonraki > bas)
            sonuc[b] = { sayfaBas: bas, sayfaSon: sonraki - 1 };
    });
    return sonuc;
}
/** Hattın OEM kitabındaki bölümü. Choke ve Kill'in 2A / 2B eşlemesi veride yok: ikisi de "2A / 2B". */
export const HAT_OEM_BOLUMLERI = { Main: ["1"], Choke: ["2A", "2B"], Kill: ["2A", "2B"], Booster: ["4"], Hydraulic: ["3"] };
/**
 * İzlenebilirlik tablosunun "OEM record" hücresi: hattın bölümleri ve sayfa aralığı. Choke ve Kill'de birleşik aralık (2A başı – 2B sonu).
 * Hesap çıkmayan kitapta sayfa null: hücre yalnız kitaba bağlanır.
 * D-059: `elle` (Project documents'ta o hat için elle girilen aralık) verilirse hesabın yerine geçer. Choke ve Kill'de hat kendi
 * aralığını gösterir: hangisinin 2A, hangisinin 2B olduğu bilinmediği için bölüm yazılmaz (`bolumler` boş).
 */
export function oemHatBolumu(hat, sayfalar, elle) {
    const bolumler = [...HAT_OEM_BOLUMLERI[hat]];
    if (elle)
        return { bolumler: bolumler.length === 1 ? bolumler : [], sayfa: elle };
    const ilk = sayfalar?.[bolumler[0]] ?? null;
    const son = sayfalar?.[bolumler[bolumler.length - 1]] ?? null;
    return { bolumler, sayfa: ilk && son ? { sayfaBas: ilk.sayfaBas, sayfaSon: son.sayfaSon } : null };
}
/**
 * OEM kitabındaki iki DNV belgesinin sayfaları (2.1): sınıf "DNV" VE ad "DNV Product Certificate" / "DNV Design Verification Report"
 * ile başlayan satır. Sınıf tek başına yetmez (bir kitapta ayraç sayfası DNV sınıfında); hızlı vites çapalarında ad uymaz, sonuç null.
 * D-059: `elle` (Project documents'ta elle girilen aralıklar; `dnv_pc` ve `oem_dvr`) verilirse o belgede hesabın yerine geçer;
 * iç dizini olmayan kitapta da 2.1 satırı böyle doğar.
 */
export function dnvSayfalari(pieceler, elle) {
    const bul = (onek) => {
        for (const p of pieceler) {
            if (p.sinif !== "DNV" || !p.ad.trim().toLowerCase().startsWith(onek))
                continue;
            const a = mrbSayfaAraligi(p.sayfa);
            if (a)
                return a;
        }
        return null;
    };
    return { urunSertifikasi: elle?.dnv_pc ?? bul("dnv product certificate"), tasarimDogrulama: elle?.oem_dvr ?? bul("dnv design verification report") };
}
/* ====================================================================================================================
 * 2 · Parça durumu (izlenebilirlik tablosu): kayıtlardan türer
 * ================================================================================================================== */
export const PARCALAR = ["box", "pipe", "pin"];
/** ST kodunun (işin `poz` değeri) etkilediği parça: boru değişimi → pipe; yeni hat siparişi → hattın tamamı. Öteki kodlarda kaynağın ek yeri. */
export const ST_PARCA_ETKISI = { "ST-03": "pipe", "ST-11": "hat" };
const EK_PARCASI = { [BOX_BAGLANTI]: "box", [BORU_BAGLANTI]: "pipe", [PIN_BAGLANTI]: "pin" };
/**
 * Kaydın hangi parçaya ait olduğu: önce işin `poz` değeri (ST kodu; eski akışta "BOX" / "PIN" / "PIPE"), sonra kaynağın ek yeri
 * (`weld.joint`). Belirlenemiyorsa null: durum hat düzeyinde bir kez yazılır.
 */
export function parcaBelirle(poz, ek) {
    const p = (poz ?? "").trim().toUpperCase();
    const st = ST_PARCA_ETKISI[p];
    if (st)
        return st;
    if (p === "BOX" || p === "PIPE" || p === "PIN")
        return p.toLowerCase();
    return EK_PARCASI[(ek ?? "").trim()] ?? null;
}
const tekilSirali = (n) => [...new Set(n)].sort((a, b) => a - b);
/**
 * Bir hattın box, pipe ve pin durumu. Öncelik: başka riserden → değişti (stok paketi) → tamir (kaynak) → original.
 * Değişim işinde (ST-03) kaynaklar değişen parçaya yazılır; komşu parça "repaired" sayılmaz. Hat düzeyinde kalan bir durum varken
 * kendi kaydı olmayan hücre "original" değil "bilinmiyor"dur.
 */
export function hatParcaDurumu(g) {
    const degisim = { box: [], pipe: [], pin: [] };
    const tamir = { box: [], pipe: [], pin: [] };
    const degisimHat = [];
    const tamirHat = [];
    for (const d of g.degisimler) {
        const p = parcaBelirle(d.poz, d.ek);
        if (p === null)
            degisimHat.push(d.paketNo);
        else
            for (const x of p === "hat" ? PARCALAR : [p])
                degisim[x].push(d.paketNo);
    }
    for (const k of g.kaynaklar) {
        const ilk = parcaBelirle(k.poz, k.ek);
        // Hattın tamamını ilgilendiren işte kaynağın yeri yine ek yerinden okunur.
        const p = ilk === "hat" ? (EK_PARCASI[(k.ek ?? "").trim()] ?? null) : ilk;
        if (p === null)
            tamirHat.push(k.no);
        else
            tamir[p].push(k.no);
    }
    const hatDuzeyi = [];
    if (degisimHat.length)
        hatDuzeyi.push({ tur: "degisti", paketNolar: tekilSirali(degisimHat) });
    if (tamirHat.length)
        hatDuzeyi.push({ tur: "tamir", kaynakNolar: tekilSirali(tamirHat) });
    const hatKokeni = g.kokenler.find((k) => k.parca === "hat")?.kokenRn ?? null;
    const parca = (p) => {
        const koken = g.kokenler.find((k) => k.parca === p)?.kokenRn ?? hatKokeni;
        if (koken)
            return { tur: "koken", rn: koken };
        if (degisim[p].length)
            return { tur: "degisti", paketNolar: tekilSirali(degisim[p]) };
        if (tamir[p].length)
            return { tur: "tamir", kaynakNolar: tekilSirali(tamir[p]) };
        return g.kayitVar && hatDuzeyi.length === 0 ? { tur: "orijinal" } : { tur: "bilinmiyor" };
    };
    return { box: parca("box"), pipe: parca("pipe"), pin: parca("pin"), hatDuzeyi };
}
/* ====================================================================================================================
 * 2 · Belgeyi kim açar
 * ================================================================================================================== */
/**
 * Belgenin bağlantısı role verilir mi. `acik` herkese; `kutuk` (kütük kaydı üzerinden gelen her belge), `kisisel` (kaynakçı ve NDT
 * inspektörü belgeleri) ve `fiyat` (müşteri siparişi, satın alma siparişi) bugün yalnız PM ve sistem yöneticisine. Kural tek yerde:
 * karar değişirse yalnız bu fonksiyon değişir. Erişimi olmayan rol satırı yine görür, bağlantıyı görmez.
 * Project documents'tan yüklenen belgeyi (`kaynak` = "proje-belgeleri"), sınıfı ne olursa olsun, o ekranın yazma yetkisi olan rol
 * de açar: kalite lideri kendi yüklediği müşteri siparişini açabilir (Bekir, 07.10.2026). Kütük üzerinden gelen belgeler bu
 * istisnaya girmez. D-062: NDT sertifikası artık kütük üzerinden gelir (Registers › NDT inspectors): kalite lideri açamaz.
 */
export function belgeErisimi(rol, sinif, kaynak) {
    if (sinif === "acik" || yoneticiMi(rol))
        return true;
    return kaynak === DOSYA_BELGE_KAYNAK_EKRAN && girdiYetkiliMi(rol, "belge");
}
/** Satırı varsayılanlarla kurar. Kişisel belgede dosya adı yazılmaz. */
export function satirKur(g) {
    const sinif = g.sinif ?? "acik";
    return {
        id: g.id,
        kalem: g.kalem,
        tur: g.tur,
        sinif,
        dokumanId: g.dokumanId ?? null,
        baglantisiz: g.dokumanId ? null : (g.baglantisiz ?? null),
        dosyaAd: sinif === "kisisel" ? null : (g.dosyaAd ?? null),
        pdf: g.pdf ?? false,
        sayfaSayisi: g.sayfaSayisi ?? null,
        sayfa: g.sayfa ?? null,
        kapsam: g.kapsam,
        hat: g.hat ?? null,
        isId: g.isId ?? null,
        kaynakNolar: g.kaynakNolar ?? [],
        adimAd: g.adimAd ?? null,
        kokenRn: g.kokenRn ?? null,
        kutuk: g.kutuk ?? null,
        ortakRnler: g.ortakRnler ?? [],
        iptalIs: g.iptalIs ?? false,
        kaynak: g.kaynak ?? null,
        kaynakRn: g.kaynakRn ?? null,
        yukleyen: g.yukleyen ?? null,
        ts: g.ts ?? null,
        kayit: g.kayit ?? null,
        hedef: g.hedef ?? null,
        beyanlar: g.beyanlar ?? [],
        beyanKalemi: g.beyanKalemi ?? null,
        ilgili: g.ilgili ?? [],
        sertlik: g.sertlik ?? false,
        dizinSatiri: g.dizinSatiri ?? null,
        // D-068: alan yalnız verildiğinde taşınır; verilmeyen satırda anahtar da yoktur.
        ...(g.olcuSayfasiEksik ? { olcuSayfasiEksik: true } : {}),
        // D-072: aynı kural.
        ...(g.beyanKonusu ? { beyanKonusu: g.beyanKonusu } : {}),
    };
}
/**
 * Satırı role göre süzer: erişimi olmayan rolde satır kalır, belge kimliği gider (`baglantisiz: "erisim"`). Dosya adı kişisel
 * belgede hiçbir role, fiyat taşıyan belgede yalnız belgeyi açabilen role yazılır; öteki sınıflarda ad her role yazılır. Ekran ve
 * Excel listesi aynı satırı okur: dosya adı kuralı yalnız buradadır. Durum ve sayaçlar role göre değişmez.
 * Kütük bağı olan satır (`kutuk` dolu) kütük üzerinden gelen belgedir: `kaynak` alanında ne yazarsa yazsın Project documents
 * istisnasına girmez (dosyanın `kaynak_ekran` kolonu serbest metindir; erişim satırın geldiği yoldan ölçülür).
 */
export function erisimUygula(s, rol) {
    const acik = belgeErisimi(rol, s.sinif, s.kutuk ? null : s.kaynak);
    const adGizli = s.sinif === "kisisel" || (s.sinif === "fiyat" && !acik);
    if (acik && !(adGizli && s.dosyaAd !== null))
        return s;
    return {
        ...s,
        dosyaAd: adGizli ? null : s.dosyaAd,
        dokumanId: acik ? s.dokumanId : null,
        baglantisiz: !acik && s.dokumanId !== null ? "erisim" : s.baglantisiz,
    };
}
/** Satırın kaynak ekran adı (İngilizce; web çevirir). Kütük belgesinde kütüğün sekmesi. Tanınmayan değerde null. */
export function satirEkranAdi(s) {
    if (s.kutuk)
        return KUTUK_EKRAN_ADI[s.kutuk.kutuk];
    return KAYNAK_EKRAN_ADI[s.kaynak ?? ""] ?? null;
}
/**
 * Kayıt ve beyan uygulama içi ekrana gider. OEM kitabının sayfa aralıklı PDF satırı uygulama içi sayfa görüntüleyicide o sayfada
 * açılır (kitaplar büyük); öteki belgeler yeni sekmede. Belge kimliği yoksa bağlantı yok.
 */
export function satirAcilisi(s) {
    if (s.hedef)
        return { tip: "rota", hedef: s.hedef };
    if (!s.dokumanId)
        return { tip: "yok" };
    if (s.kaynak === "mrb" && s.pdf && s.sayfa)
        return { tip: "goruntuleyici", dokumanId: s.dokumanId, sayfa: s.sayfa.sayfaBas };
    return { tip: "sekme", dokumanId: s.dokumanId, sayfa: s.sayfa?.sayfaBas ?? null };
}
export const BOS_EK_GIRDILER = { belgeler: [], kokenler: [], izBaglari: [] };
/**
 * Ek girdi belgesi bu riserde görünür mü: proje hepsinde; iş emri numarası tutanda; riser kendi RN'inde. Sözlükte olmayan kapsam
 * (ör. veritabanında duran eski "ndt_personel" satırı) hiçbir riserde görünmez.
 */
export function girdiBelgesiUyarMi(b, r) {
    if (b.kapsam === "proje")
        return true;
    if (b.kapsam === "is_emri")
        return b.anahtar !== null && r.isEmri !== null && b.anahtar === r.isEmri;
    if (b.kapsam === "riser")
        return b.anahtar === r.rn;
    return false;
}
const GIRDI_KAPSAM_CIPI = { proje: "proje", is_emri: "is_emri", riser: "riser" };
/** Ek girdi belgesinin satırı: kalem ve erişim sınıfı türden, kapsam çipi kapsamdan; kaynak ekran "Project documents". */
export function girdiBelgeSatiri(b, d) {
    const t = DOSYA_GIRDI_TURLERI[b.tur];
    return satirKur({
        id: `girdi:${b.id}`,
        kalem: t.kalem === "ref" ? null : t.kalem,
        tur: b.tur,
        sinif: t.sinif,
        kapsam: GIRDI_KAPSAM_CIPI[b.kapsam],
        dokumanId: b.dokumanId,
        dosyaAd: d?.ad ?? null,
        pdf: d?.pdf ?? false,
        sayfaSayisi: d?.sayfaSayisi ?? null,
        sayfa: b.sayfaBas !== null && b.sayfaSon !== null ? { sayfaBas: b.sayfaBas, sayfaSon: b.sayfaSon } : null,
        kaynak: DOSYA_BELGE_KAYNAK_EKRAN,
        yukleyen: b.kim,
        ts: b.ts,
    });
}
export const yuvaAnahtari = (y) => [y.tur, y.hat ?? "", y.rn ?? "", y.isId ?? "", y.kaynakNo ?? "", y.kod ?? "", y.satirId ?? ""].join("|");
/** İmzalayanın sertifikası beklenen NDT raporu türleri (5.4). Yüklemede imzalayan da bu türlerde sorulur (`imzalayanSorulurMu`). */
export const NDT_RAPOR_TURLERI = ["hat_ndt", "is_ndt", "bevel_ndt", "kaynak_sonrasi_ndt", "pwht_sonrasi_ndt", "basinc_sonrasi_dpi"];
/** Türü kalemin yuvasıyla aynı olan tek belgeli yuvalar. */
const TEK_YUVA = {
    osi_coc: "1.1", musteri_siparisi: "1.2", bom: "1.3", montaj_resmi: "1.4", kimlik_plakasi: "1.5",
    dnv_urun_sertifikasi: "2.1", dvr_oem: "2.1", dvr_osi: "2.1", dnv_survey_report: "2.2",
    muayene_raporu: "3.2", kalite_plani: "3.3", fm0012: "3.4", boya_raporu: "3.6", prosedur: "5.6",
};
/**
 * Satırın doldurduğu yuvalar. Şart olmayan belge (Rework Scope, FM-0011, hat NDT'si, süreklilik kaydı …) yuva doldurmaz, yalnız
 * listelenir. Reference satırı ve iptal edilmiş işin satırı hiçbir yuvayı doldurmaz.
 */
export function satirYuvalari(s) {
    if (s.kalem === null || s.iptalIs)
        return [];
    const t = s.tur;
    if (TEK_YUVA[t] === s.kalem)
        return [{ tur: t }];
    const isId = s.isId;
    switch (s.kalem) {
        case "3.1": return [{ tur: "malzeme_sertifika_listesi" }];
        case "3.2": return t === "verici_muayene_raporu" && s.kokenRn ? [{ tur: t, rn: s.kokenRn }] : [];
        case "3.5": return t === "basinc_testi" && s.hat && BASINC_TESTI_HATLARI.includes(s.hat) ? [{ tur: t, hat: s.hat }] : [];
        case "4.1": return isId ? [{ tur: "malzeme_sertifikasi", isId }] : [];
        case "4.2": return isId ? [{ tur: "bevel_ndt", isId }] : [];
        case "4.3":
            if (!isId)
                return [];
            if (t === "fitup_kaydi")
                return s.kaynakNolar.map((kaynakNo) => ({ tur: t, isId, kaynakNo }));
            return t === "weld_map" ? [{ tur: t, isId }] : [];
        case "4.4": return isId ? [{ tur: "pwht_kaydi", isId }] : [];
        case "4.5": return isId ? [{ tur: "kaynak_sonrasi_ndt", isId }] : [];
        case "5.1": return s.kutuk ? [{ tur: "wps_pqr", kod: s.kutuk.kod }] : [];
        case "5.2": return s.kutuk ? [{ tur: "sarf_sertifikasi", kod: s.kutuk.kod }] : [];
        case "5.3": return t === "kaynakci_yeterlilik" && s.kutuk ? [{ tur: t, kod: s.kutuk.kod }] : [];
        // Sertifika yalnız kendi inspektörünün imzaladığı raporların yuvasını doldurur (yuva rapor ve imzalayan başınadır; `kod` =
        // inspektörün kimliği). Kütük bağı olmayan satır yuva doldurmaz: 5.4 belgesi yalnız Registers › NDT inspectors'tan gelir.
        case "5.4": {
            const kod = s.kutuk?.kod;
            return t === "ndt_yeterlilik" && kod ? s.ilgili.map((satirId) => ({ tur: t, satirId, kod })) : [];
        }
        case "5.5":
            if (t === "kalibrasyon_test")
                return s.ilgili.map((satirId) => ({ tur: t, satirId }));
            return t === "kalibrasyon_fitup" ? [{ tur: t }] : [];
        default: return [];
    }
}
const BEYAN_EKRANI = { adim: "komponent", hat_ndt: "assembly", fitup: "fitup", teslim: "teslim", muayene: "muayene" };
/**
 * Beyanın kapattığı yuvalar; `is` beyanın kaynaklı işidir (kaynağı olmayan ya da bilinmeyen işte null). Beyan yalnız ait olduğu
 * yuvayı kapatır: bevel, PWHT ve kaynak sonrası test adımı işin o yuvasını; fit-up adımları işin fit-up kayıtlarını; kaynak adımı
 * işin weld map'ini; hidrotest adımı hattın basınç testini. Öteki beyanlar (hat NDT'si, post-test, fotoğraf, ölçüm, teslim)
 * şart olan bir yuvaya ait değildir: yalnız 5.6'da listelenir.
 */
export function beyanYuvalari(b, is) {
    if (b.kaynak !== "adim" || !b.adimAd)
        return [];
    const k = beyanKalemi(b.adimAd);
    if (k === "3.5")
        return b.hat && BASINC_TESTI_HATLARI.includes(b.hat) ? [{ tur: "basinc_testi", hat: b.hat }] : [];
    if (!is)
        return [];
    const isId = is.id;
    if (k === "4.2")
        return [{ tur: "bevel_ndt", isId }];
    if (k === "4.4")
        return [{ tur: "pwht_kaydi", isId }];
    if (k === "4.5")
        return [{ tur: "kaynak_sonrasi_ndt", isId }];
    if (k === "4.3")
        return kaynakAdimiMi(b.adimAd) ? [{ tur: "weld_map", isId }] : is.kaynaklar.map((w) => ({ tur: "fitup_kaydi", isId, kaynakNo: w.no }));
    return [];
}
/**
 * Beyanın kalemi: yuvasını kapattığı (ya da listelendiği) kalem. Kaynağı olmayan işte Bölüm 4 kalemi blok açmaz.
 * D-072: muayene bekleyen kalemin beyanı hat NDT'si gibi 3.2'de (Inspection Survey Report) listelenir, yuvayı kapatmaz.
 */
function beyaninKalemi(b, is) {
    if (b.kaynak === "hat_ndt" || b.kaynak === "muayene")
        return "3.2";
    if (b.kaynak !== "adim" || !b.adimAd)
        return "5.6";
    const k = beyanKalemi(b.adimAd);
    if (bolumu(k) !== "4" || is)
        return k;
    const y = kaynaksizIsKalemi(k);
    return y === "ref" ? "5.6" : y;
}
function beyanSatiri(b, is) {
    const kaynakNolar = b.kaynakNolar ?? [];
    return satirKur({
        id: `beyan:${b.id}`,
        kalem: "5.6",
        tur: "beyan",
        kapsam: b.kaynak === "hat_ndt" ? "hat" : b.kaynak === "teslim" ? "riser" : b.kaynak === "muayene" ? "parca" : kaynakNolar.length ? "kaynak" : "is",
        hat: b.hat ?? null,
        isId: b.isId ?? null,
        kaynakNolar,
        adimAd: b.adimAd ?? null,
        kaynak: BEYAN_EKRANI[b.kaynak],
        yukleyen: b.kim ?? null,
        ts: b.ts ?? null,
        kayit: "beyan",
        hedef: b.hedef,
        beyanlar: [b.tur],
        beyanKalemi: beyaninKalemi(b, is),
        ...(b.konu ? { beyanKonusu: b.konu } : {}),
    });
}
/** Kaynak onarımı var mı: iptal edilmemiş, riser grubundaki en az bir işte kaynak var. */
export const kaynakOnarimiVar = (o) => o.isler.some((i) => i.kaynaklar.length > 0);
/**
 * 30 kalemin beklenen yuvaları ve "doğmaz" kuralları. `satirlar` Bölüm 1–5'e yerleşmiş satırlardır (5.4 ve 5.5'in yuvaları
 * var olan NDT raporlarından ve basınç testi sertifikalarından doğar; 4.1'in yuvası malzeme gerektirmeyen kaynaklı işte de
 * paket belgesi varsa açılır). 4.8 ve 5.6'nın durumu `dosyaKur`da ayrıca kurulur.
 * D-059: 5.4 ve 5.5'in yuvaları canlı rapor kümesinden doğar (`canliRaporTipi`: Project documents'ta bağ verilebilen satırlar);
 * belge kimliği olmayan kayıt (`docs[adım] === true`) yuva açmaz, çünkü ona imzalayan ya da ekipman bağlanamaz. 5.4'ün yuvası rapor
 * ve imzalayan başınadır: imzalayanı girilmemiş raporda tek yuva (kodsuz), girilmişse her imzalayan için bir yuva (`kod`); böylece
 * sertifikası olmayan imzalayan eksikte görünür ve iki imzalı raporu tek kayıt kapatmaz.
 * D-062: 5.4 yuvasının `kod`'u inspektörün kimliğidir (olgudan ve sertifika satırının kütük bağından); `ad` yalnız gösterim için
 * `inspektorAdlari`'ndan eklenir. Yuva, inspektörün en az bir etkin sertifika satırıyla dolar.
 */
export function beklenenYuvalar(o, satirlar) {
    const kaynakli = o.isler.filter((i) => i.kaynaklar.length > 0);
    const onarim = kaynakli.length > 0;
    /** Kalemde satırı olan iş: belgesi listelenen kalem "doğmaz" olmaz. */
    const belgeli = (kalem, i) => satirlar.some((s) => s.kalem === kalem && s.isId === i.id && !s.iptalIs);
    const var_ = (yuvalar) => ({ yuvalar, dogmaz: null });
    const yok = (dogmaz) => ({ yuvalar: [], dogmaz });
    /** Bölüm 4 ve 5.1–5.3: kaynak onarımı yoksa doğmaz; yuva çıkmıyorsa verilen nedenle doğmaz (neden yoksa eksik). */
    const onarimda = (yuvalar, bosNedeni) => {
        if (!onarim)
            return yok("kaynak_onarimi_yok");
        const y = yuvalar();
        return y.length === 0 && bosNedeni ? yok(bosNedeni) : var_(y);
    };
    const adimli = (kalem) => kaynakli.filter((i) => i.adimlar.some((a) => adimKalemi(a) === kalem));
    const kullanilan = (kutuk) => [...new Set(o.kutukKullanimlari.filter((k) => k.kutuk === kutuk).map((k) => k.kod))];
    const ndtRaporlari = satirlar.filter((s) => canliRaporTipi(s) === "ndt");
    const sertifikalar = satirlar.filter((s) => canliRaporTipi(s) === "basinc_testi");
    /** Raporun imzalayanları (inspektör kimlikleri): rapordaki imzalayan bağı (olgu) ve o raporu `ilgili` gösteren sertifika satırlarının inspektörü. */
    const imzalayanlar = (satirId) => [
        ...new Set([
            ...(o.ndtImzalayanlari ?? []).filter((x) => x.satirId === satirId).flatMap((x) => x.kodlar),
            ...satirlar.filter((s) => s.kalem === "5.4" && s.tur === "ndt_yeterlilik" && s.kutuk !== null && s.ilgili.includes(satirId)).map((s) => s.kutuk.kod),
        ]),
    ];
    /** İnspektörün adı (yalnız gösterim); haritada yoksa yuvaya ad yazılmaz. */
    const inspektorAdi = (kod) => {
        const ad = o.inspektorAdlari && Object.hasOwn(o.inspektorAdlari, kod) ? o.inspektorAdlari[kod] : null;
        return typeof ad === "string" && ad.trim() ? ad : null;
    };
    const yeterlilikYuvalari = (s) => {
        const kodlar = imzalayanlar(s.id);
        if (!kodlar.length)
            return [{ tur: "ndt_yeterlilik", satirId: s.id }];
        return kodlar.map((kod) => {
            const ad = inspektorAdi(kod);
            return { tur: "ndt_yeterlilik", satirId: s.id, kod, ...(ad ? { ad } : {}) };
        });
    };
    return {
        "1.1": var_([{ tur: "osi_coc" }]),
        "1.2": var_([{ tur: "musteri_siparisi" }]),
        "1.3": var_([{ tur: "bom" }]),
        "1.4": var_([{ tur: "montaj_resmi" }]),
        "1.5": var_([{ tur: "kimlik_plakasi" }]),
        "2.1": var_([{ tur: "dnv_urun_sertifikasi" }, { tur: "dvr_oem" }, { tur: "dvr_osi" }]),
        "2.2": var_([{ tur: "dnv_survey_report" }]),
        "3.1": var_([{ tur: "malzeme_sertifika_listesi" }]),
        "3.2": var_([{ tur: "muayene_raporu" }, ...o.vericiRnler.map((rn) => ({ tur: "verici_muayene_raporu", rn }))]),
        "3.3": var_([{ tur: "kalite_plani" }]),
        "3.4": var_([{ tur: "fm0012" }]),
        "3.5": var_(BASINC_TESTI_HATLARI.map((hat) => ({ tur: "basinc_testi", hat }))),
        "3.6": var_([{ tur: "boya_raporu" }]),
        "3.7": var_([{ tur: "boya_uygunluk" }]),
        "3.8": yok("kapsam_disi"),
        "3.9": var_([{ tur: "fm0012" }]),
        // Malzeme gerektirmeyen işte de fit-up'ta stok paketi seçilebilir: paketin belgesi varsa o işin yuvası da açılır (kalem "dahil" olur).
        "4.1": onarimda(() => kaynakli.filter((i) => i.malzemeGerekli || belgeli("4.1", i)).map((i) => ({ tur: "malzeme_sertifikasi", isId: i.id })), "malzeme_gerekmiyor"),
        "4.2": onarimda(() => adimli("4.2").map((i) => ({ tur: "bevel_ndt", isId: i.id })), "adim_yok"),
        "4.3": onarimda(() => kaynakli.flatMap((i) => [...i.kaynaklar.map((w) => ({ tur: "fitup_kaydi", isId: i.id, kaynakNo: w.no })), { tur: "weld_map", isId: i.id }])),
        "4.4": onarimda(() => adimli("4.4").map((i) => ({ tur: "pwht_kaydi", isId: i.id })), "adim_yok"),
        "4.5": onarimda(() => adimli("4.5").map((i) => ({ tur: "kaynak_sonrasi_ndt", isId: i.id })), "adim_yok"),
        "4.6": yok("kapsam_disi"),
        "4.7": yok("istenmiyor"),
        "4.8": onarim ? var_([{ tur: "boya_raporu" }]) : yok("kaynak_onarimi_yok"),
        "5.1": onarimda(() => kullanilan("wps").map((kod) => ({ tur: "wps_pqr", kod }))),
        "5.2": onarimda(() => kullanilan("sarf").map((kod) => ({ tur: "sarf_sertifikasi", kod }))),
        "5.3": onarimda(() => kullanilan("kaynakci").map((kod) => ({ tur: "kaynakci_yeterlilik", kod }))),
        "5.4": var_(ndtRaporlari.flatMap(yeterlilikYuvalari)),
        "5.5": var_(sertifikalar.length ? sertifikalar.map((s) => ({ tur: "kalibrasyon_test", satirId: s.id })) : [{ tur: "kalibrasyon_fitup" }]),
        "5.6": yok("beyan_yok"),
    };
}
/** Dosyanın sonunda düzenlenen kalemler: belge yokken "Issued at the end" (hiç "Missing" olmaz). */
const SONDA_DUZENLENEN = new Set(["1.1", "2.2"]);
/**
 * Kalemin durumu, beklenen yuvalarının halinden.
 * - Hiçbir yuva belgeyle dolu değilse sırayla: 1.1 ve 2.2 → sonda; "doğmaz" kuralı tutuyorsa → doğmaz; bütün yuvalar beyanla
 *   kapalıysa → beyan; kalan → eksik.
 * - En az bir yuva doluysa: boş yuva varsa → eksik (boş yuvalar adıyla); boş yuva yok ama beyanla kapalı yuva varsa → beyan;
 *   hepsi doluysa → dahil.
 * Beyan yalnız kendi yuvasını kapatır: başka bir yuva boşsa kalem eksiktir. Hiçbir durum bir adımı kilitlemez.
 */
export function kalemDurumu(g) {
    const bos = g.yuvalar.filter((y) => y.hal === "bos").map((y) => y.yuva);
    const beyanli = g.yuvalar.some((y) => y.hal === "beyan");
    if (!g.yuvalar.some((y) => y.hal === "dolu")) {
        if (SONDA_DUZENLENEN.has(g.no))
            return { durum: "sonda", eksik: [] };
        if (g.dogmaz)
            return { durum: "dogmaz", eksik: [] };
        if (beyanli && bos.length === 0)
            return { durum: "beyan", eksik: [] };
        return { durum: "eksik", eksik: bos };
    }
    if (bos.length)
        return { durum: "eksik", eksik: bos };
    return { durum: beyanli ? "beyan" : "dahil", eksik: [] };
}
export function dosyaSayaclari(durumlar) {
    const say = (d) => durumlar.filter((x) => x === d).length;
    const s = { dahil: say("dahil"), beyan: say("beyan"), sonda: say("sonda"), eksik: say("eksik"), dogmaz: say("dogmaz") };
    return { ...s, toplam: durumlar.length, kapsanan: s.dahil + s.beyan + s.sonda };
}
/** "Used on these welds" satırlarının sırası (statik sayfadaki gibi: WPS, kaynakçı, sarf). */
const KUTUK_SATIR_SIRASI = ["5.1", "5.3", "5.2"];
const kalemSirala = (satirlar, sira = (k) => KALEM_SIRASI.get(k) ?? 0) => satirlar
    .map((s, i) => ({ s, i }))
    .sort((a, b) => sira(a.s.kalem) - sira(b.s.kalem) || a.i - b.i)
    .map((x) => x.s);
/**
 * Dosyayı kurar: satırları kalemlere, bloklara ve Reference'a yerleştirir; beyanları 5.6 satırı yapar; 30 kalemin durumunu ve
 * sayaçları hesaplar. Kurallar:
 * - İptal edilmiş işin satırı ve kalemi olmayan satır Reference'tadır; listeye ve sayıya girmez.
 * - Bölüm 4 satırı işinin bloğundadır. Kaynağı olmayan işin satırı blok açmaz (`kaynaksizIsKalemi`): yalnız listelenir, yuva doldurmaz;
 *   3.2'ye yönlenen test raporu nötr adla listelenir (`kaynaksizIsTuru`).
 * - 5.1, 5.2, 5.3 satırları Bölüm 4'ün kütük satırlarında çizilir ve orada sayılır; listede kendi kalemlerindedir. Kaynak onarımı
 *   görmemiş riserde bu kalemler doğmaz ve Bölüm 4 boştur: gelen satır (ör. kaynaksız işe girilen ek sarfın sertifikası) Reference'tadır.
 * - 3.9 FM-0012 yüklüyse dahildir ve FM-0012'ye bağlanır; "Documents on file"da ayrı satırı doğmaz.
 * - 4.8, kaynak onarımı varsa 3.6'nın durumunu alır.
 * - 5.6: etkin beyan varsa beyan (prosedür de listelenir); beyan yok, prosedür varsa dahil; ikisi de yoksa doğmaz.
 */
export function dosyaKur(g) {
    const o = g.olgular;
    const kaynakliIs = new Map(o.isler.filter((i) => i.kaynaklar.length > 0).map((i) => [i.id, i]));
    const kullanim = new Map(o.kutukKullanimlari.map((k) => [`${k.kutuk}|${k.kod}`, k.yerler]));
    const yerlesik = [];
    const referans = [];
    const dolu = new Set();
    for (const ham of g.satirlar) {
        const s = ham.kutuk && ham.kutuk.kullanim.length === 0 ? { ...ham, kutuk: { ...ham.kutuk, kullanim: kullanim.get(`${ham.kutuk.kutuk}|${ham.kutuk.kod}`) ?? [] } } : ham;
        if (s.iptalIs || s.kalem === null) {
            referans.push(s.kalem === null ? s : { ...s, kalem: null });
            continue;
        }
        if (bolumu(s.kalem) === "4" && !(s.isId !== null && kaynakliIs.has(s.isId))) {
            const k = kaynaksizIsKalemi(s.kalem);
            if (k === "ref")
                referans.push({ ...s, kalem: null });
            else
                yerlesik.push({ ...s, kalem: k, tur: kaynaksizIsTuru(s.tur) });
            continue;
        }
        if (kaynakliIs.size === 0 && KUTUK_SATIR_SIRASI.includes(s.kalem)) {
            referans.push({ ...s, kalem: null });
            continue;
        }
        yerlesik.push(s);
        for (const y of satirYuvalari(s))
            dolu.add(yuvaAnahtari(y));
    }
    const beyanli = new Set();
    const beyanSatirlari = g.beyanlar.map((b) => {
        const is = b.isId ? (kaynakliIs.get(b.isId) ?? null) : null;
        for (const y of beyanYuvalari(b, is))
            beyanli.add(yuvaAnahtari(y));
        return beyanSatiri(b, is);
    });
    const kalemSatirlari = (no) => yerlesik.filter((s) => s.kalem === no);
    const beklenen = beklenenYuvalar(o, yerlesik);
    const sonuclar = new Map();
    for (const k of DOSYA_KALEMLERI) {
        const b = beklenen[k.no];
        const yuvalar = b.yuvalar.map((yuva) => {
            const a = yuvaAnahtari(yuva);
            return { yuva, hal: dolu.has(a) ? "dolu" : beyanli.has(a) ? "beyan" : "bos" };
        });
        sonuclar.set(k.no, kalemDurumu({ no: k.no, yuvalar, dogmaz: b.dogmaz !== null }));
    }
    if (beyanSatirlari.length)
        sonuclar.set("5.6", { durum: "beyan", eksik: [] });
    else if (kalemSatirlari("5.6").length)
        sonuclar.set("5.6", { durum: "dahil", eksik: [] });
    const liste = DOSYA_KALEMLERI.map((k) => {
        const r = sonuclar.get(k.no);
        const satirlar = k.no === "3.9" ? yerlesik.filter((s) => s.tur === "fm0012" && s.kalem === "3.4") : k.no === "5.6" ? [...kalemSatirlari("5.6"), ...beyanSatirlari] : kalemSatirlari(k.no);
        return {
            no: k.no,
            bolum: k.bolum,
            ad: k.ad,
            durum: r.durum,
            eksik: r.eksik,
            satirlar,
            beyanlar: k.no === "5.6" ? [] : beyanSatirlari.filter((s) => s.beyanKalemi === k.no),
            atif: k.no === "3.9" ? "3.4" : k.no === "4.8" ? "3.6" : null,
            dogmazNedeni: r.durum === "dogmaz" ? beklenen[k.no].dogmaz : null,
        };
    });
    const bolumler = DOSYA_BOLUMLERI.map((b) => {
        const gruplar = DOSYA_KALEM_GRUPLARI.filter((x) => x.bolum === b.no).map((x) => ({
            kalemler: x.kalemler.map((no) => ({ no, ad: kalemTanimi(no).ad })),
            not: x.not ?? null,
            // 5.1, 5.2, 5.3 belgeleri Bölüm 4'te çizilir; 5.6'da beyanlar belgelerden sonra.
            satirlar: x.not === "bolum_4" ? [] : [...kalemSirala(yerlesik.filter((s) => x.kalemler.includes(s.kalem))), ...(x.kalemler.includes("5.6") ? beyanSatirlari : [])],
        }));
        const bloklar = b.no === "4"
            ? [...kaynakliIs.values()].map((i, n) => ({
                sira: n + 1,
                isId: i.id,
                hat: i.hat,
                poz: i.poz,
                is: i.is,
                kaynaklar: [...i.kaynaklar].sort((x, y) => x.no - y.no),
                satirlar: kalemSirala(yerlesik.filter((s) => bolumu(s.kalem) === "4" && s.isId === i.id)),
            }))
            : [];
        const kutukSatirlari = b.no === "4" ? kalemSirala(yerlesik.filter((s) => KUTUK_SATIR_SIRASI.includes(s.kalem)), (k) => KUTUK_SATIR_SIRASI.indexOf(k)) : [];
        return {
            no: b.no,
            ad: b.ad,
            belgeSayisi: gruplar.reduce((n, x) => n + x.satirlar.length, 0) + bloklar.reduce((n, x) => n + x.satirlar.length, 0) + kutukSatirlari.length,
            kalemSayisi: DOSYA_KALEMLERI.filter((k) => k.bolum === b.no).length,
            gruplar,
            bloklar,
            kutukSatirlari,
        };
    });
    return {
        bolumler,
        referans,
        liste,
        sayac: dosyaSayaclari(liste.map((k) => k.durum)),
        belgeSayisi: bolumler.reduce((n, b) => n + b.belgeSayisi, 0),
    };
}
/* ====================================================================================================================
 * 5 · Project documents (D-059): sekmeler, tür listeleri, yetki, içerik türü
 * ================================================================================================================== */
/**
 * Project documents ekranının iki sekmesi; sıra ekrandaki hap sırasıdır. D-063: üçüncü sekme ("NDT personnel") kalktı; o işin yeni
 * kapısı Registers › NDT inspectors'tır.
 */
export const GIRDI_SEKMELERI = ["proje", "riser"];
/** Sekme adları (statik sayfadaki haplar). */
export const GIRDI_SEKME_ADI = { proje: "Project and work order", riser: "Riser" };
/**
 * Sekmenin sunduğu belge türleri, statik sayfadaki sırayla: "Add document" penceresinin Type seçenekleri · "Riser documents"
 * satırları. Her tür tam bir sekmededir.
 */
export const GIRDI_SEKME_TURLERI = {
    proje: ["musteri_siparisi", "bom", "montaj_resmi", "dvr_osi", "kalite_plani", "prosedur"],
    riser: ["osi_coc", "dnv_survey_report", "kimlik_plakasi", "malzeme_sertifika_listesi", "onceki_cevrim"],
};
/** Sekmenin kapsamları. */
export const GIRDI_SEKME_KAPSAMLARI = {
    proje: ["proje", "is_emri"],
    riser: ["riser"],
};
/**
 * Türün Project documents ekranındaki adı (statik sayfadaki metinlerle birebir). Riser Documentation'daki satır adı ayrıdır
 * (`DOSYA_TUR_ADI`).
 */
export const DOSYA_GIRDI_TUR_ADI = {
    musteri_siparisi: "Customer purchase order",
    bom: "Bill of materials",
    montaj_resmi: "General arrangement drawing",
    dvr_osi: "DNV Design Verification Report — Oil States design",
    kalite_plani: "Quality inspection and test plan",
    prosedur: "Procedure",
    osi_coc: "OSI Certificate of Conformance",
    dnv_survey_report: "DNV Survey Report",
    kimlik_plakasi: "Identification plate photos",
    malzeme_sertifika_listesi: "Material certification list (OSIUK-Q-FM-0024)",
    onceki_cevrim: "Previous cycle documents",
};
export const girdiTuruMu = (t) => typeof t === "string" && Object.hasOwn(DOSYA_GIRDI_TURLERI, t);
export const girdiKapsamiMi = (k) => typeof k === "string" && DOSYA_GIRDI_KAPSAMLARI.includes(k);
/** Türün sekmesi. */
export function girdiSekmesi(tur) {
    return GIRDI_SEKMELERI.find((s) => GIRDI_SEKME_TURLERI[s].includes(tur));
}
/**
 * Türün ekranda sunulan (ve yazımda kabul edilen) kapsamları: sözlükteki kapsamlarından sekmesinde olanlar. Kalite planının riser
 * kapsamı sözlükte durur ama ekranda sunulmaz: yazımda da kabul edilmez.
 */
export function girdiKapsamlari(tur) {
    const sekme = GIRDI_SEKME_KAPSAMLARI[girdiSekmesi(tur)];
    return DOSYA_GIRDI_TURLERI[tur].kapsamlar.filter((k) => sekme.includes(k));
}
/** Tekil tür: aynı kapsam ve anahtarda tek etkin belge (müşteri siparişi, CoC, DNV Survey Report); yenisi için önce eskisi kaldırılır. */
export const girdiTekilMi = (tur) => DOSYA_GIRDI_TURLERI[tur].tekil;
/**
 * Project documents yazma yetkileri (yeni yetki anahtarı yok). Her işlem listedeki yetkilerden BİRİYLE yapılır: belge, köken ve OEM
 * sayfası `mrb` (kalite lideri); kimlik plakası fotoğrafı (yükleme, bağlama, kaldırma) ve rapor ekipman / imzalayan bağı `evrak` ya da
 * `mrb` (saha inspektörü de). PM ve sistem yöneticisi her işlemde yetkilidir (`yetkiliMi`).
 */
export const GIRDI_YETKILERI = {
    belge: ["mrb"],
    plaka: ["evrak", "mrb"],
    koken: ["mrb"],
    oemSayfa: ["mrb"],
    iz: ["evrak", "mrb"],
};
/** Belge türünün yazma işlemi: kimlik plakası fotoğrafı `plaka`, öteki türler `belge`. */
export const girdiIslemi = (tur) => (tur === "kimlik_plakasi" ? "plaka" : "belge");
/** Rol bu işlemi yapabilir mi. Sunucu (uç içi denetim) ve web (pasif düğme, ipucu) aynı kuralı okur. */
export function girdiYetkiliMi(rol, islem) {
    return GIRDI_YETKILERI[islem].some((i) => yetkiliMi(rol, i));
}
/** Yüklenen dosyanın içerik türü. Başlık baytlarından okunur: ad ya da MIME yetmez. */
export const DOSYA_ICERIK_TURLERI = ["pdf", "jpeg", "png"];
const ICERIK_IMZALARI = [
    { tur: "pdf", imza: [0x25, 0x50, 0x44, 0x46, 0x2d] }, // %PDF-
    { tur: "jpeg", imza: [0xff, 0xd8, 0xff] },
    { tur: "png", imza: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
];
/** Dosyanın ilk baytlarından içerik türü (Buffer ya da Uint8Array); PDF, JPEG ya da PNG değilse null. */
export function dosyaIcerikTuru(baytlar) {
    if (!baytlar)
        return null;
    return ICERIK_IMZALARI.find((x) => baytlar.length >= x.imza.length && x.imza.every((b, i) => baytlar[i] === b))?.tur ?? null;
}
/** Türün kabul ettiği içerikler: kimlik plakası fotoğrafı JPEG, PNG ya da PDF; öteki her tür yalnız PDF. */
export function girdiIcerikTurleri(tur) {
    return tur === "kimlik_plakasi" ? DOSYA_ICERIK_TURLERI : ["pdf"];
}
/** Yüklenen dosyanın içeriği türe uymuyorsa hata cümlesi. */
export function girdiIcerikHatasi(tur, icerik) {
    if (icerik !== null && girdiIcerikTurleri(tur).includes(icerik))
        return null;
    return tur === "kimlik_plakasi" ? "The identification plate photo must be a JPEG, PNG or PDF file." : "The document must be a PDF.";
}
/** Dosya seçme penceresinin `accept` değeri. */
export const girdiDosyaKabul = (tur) => (tur === "kimlik_plakasi" ? "image/jpeg,image/png,.pdf" : ".pdf");
/* ====================================================================================================================
 * 5 · Project documents: riser numarası, iş emirleri, "kaç risere uygulanır"
 * ================================================================================================================== */
/** Riser numarasını okur: "120", "r120", " R002 " → "R120", "R120", "R002" (`riserRnBicimi`); biçim tutmuyorsa null. */
export function riserRnOku(ham) {
    const m = /^R?(\d{1,3})$/.exec(typeof ham === "string" ? ham.trim().toUpperCase() : "");
    return m && Number(m[1]) >= 1 ? riserRnBicimi(m[1]) : null;
}
/** Doğal sıra: metnin içindeki sayılar sayı olarak karşılaştırılır ("9", "10"dan önce). */
const dogalSira = (a, b) => a.localeCompare(b, "en", { numeric: true });
const duzSira = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
/**
 * Bilinen iş emirleri (numara elle yazılmaz, buradan seçilir): muayene setlerinin müşteri siparişi metninden okunan numaralar
 * (`isEmriNo`) ve dolu batch iş emri alanları. Riser sayısı, iş emri o numara olan riserlerin sayısıdır; riseri olmayan numara da
 * listelenir (0). Numara sırasıyla.
 */
export function isEmriSecenekleri(g) {
    const nolar = new Set();
    for (const m of g.musteriPolar) {
        const no = isEmriNo(m);
        if (no)
            nolar.add(no);
    }
    for (const w of g.batchWolar) {
        const no = (w ?? "").trim();
        if (no)
            nolar.add(no);
    }
    for (const r of g.riserler)
        if (r.isEmri)
            nolar.add(r.isEmri);
    return [...nolar].sort(dogalSira).map((no) => ({ no, riserSayisi: g.riserler.filter((r) => r.isEmri === no).length }));
}
/** İş emri bilinen numaralardan değilse hata cümlesi. */
export function isEmriHatasi(no, bilinenler) {
    if (!no)
        return "Select the work order.";
    return bilinenler.includes(no) ? null : `Work order ${no} is not in the list of known work orders.`;
}
/**
 * Belge kaç risere uygulanır: Riser Documentation'da göründüğü riserlerin sayısı (`girdiBelgesiUyarMi`). Proje: bütün riserler;
 * iş emri: o iş emrindeki riserler; riser: kendisi.
 */
export function uygulananRiserSayisi(b, riserler) {
    return riserler.filter((r) => girdiBelgesiUyarMi(b, r)).length;
}
const sayfaNoOku = (v) => (v === null || v === undefined || v === "" ? null : typeof v === "number" && Number.isInteger(v) ? v : undefined);
/**
 * Bağ gövdesini denetler ve normalleştirir (veri erişimi gerektirmeyen kurallar): tür ve tür × kapsam uyumu; anahtar kapsama göre
 * (proje: boş · iş emri: dolu · riser: RN biçimi); sayfalar ikisi birden ya da hiçbiri, 1'den başlar, ilk sayfa sondan sonra
 * olamaz; not kırpılır. İş emrinin bilinen numaralardan olması (`isEmriHatasi`), dosya (`girdiDosyasiHatasi`) ve çakışma
 * (`girdiCakismaHatasi`) ayrıca denetlenir.
 * D-063: tür "ndt_yeterlilik" sözlükte olmadığı için "Unknown document type." ile, kapsam "ndt_personel" öteki türlerle
 * "This document type cannot be attached to the chosen scope." ile reddedilir.
 */
export function dosyaBelgeHazirla(ham) {
    const hata = (m) => ({ taslak: null, hata: m });
    if (!girdiTuruMu(ham.tur))
        return hata("Unknown document type.");
    const tur = ham.tur;
    if (!girdiKapsamiMi(ham.kapsam) || !girdiKapsamlari(tur).includes(ham.kapsam))
        return hata("This document type cannot be attached to the chosen scope.");
    const kapsam = ham.kapsam;
    let anahtar = null;
    if (kapsam === "is_emri") {
        anahtar = typeof ham.anahtar === "string" ? ham.anahtar.trim() || null : null;
        if (!anahtar)
            return hata("Select the work order.");
    }
    else if (kapsam === "riser") {
        anahtar = riserRnOku(ham.anahtar);
        if (!anahtar)
            return hata("Riser number is not valid.");
    }
    const dokumanId = typeof ham.dokumanId === "string" ? ham.dokumanId.trim() : "";
    if (!dokumanId)
        return hata("Select the file.");
    const sayfaBas = sayfaNoOku(ham.sayfaBas);
    const sayfaSon = sayfaNoOku(ham.sayfaSon);
    if (sayfaBas === undefined || sayfaSon === undefined)
        return hata("Pages: write whole page numbers.");
    if ((sayfaBas === null) !== (sayfaSon === null))
        return hata("Pages: give both the first and the last page, or neither.");
    if (sayfaBas !== null && sayfaSon !== null) {
        if (sayfaBas < 1)
            return hata("Pages: pages start at 1.");
        if (sayfaBas > sayfaSon)
            return hata("Pages: the first page must not be after the last page.");
    }
    const not = typeof ham.not === "string" ? ham.not.trim() || null : null;
    return { taslak: { kapsam, anahtar, tur, dokumanId, sayfaBas, sayfaSon, not }, hata: null };
}
/**
 * Bağlanacak dosyanın denetimi. `d` dosya kaydıdır (yoksa ya da silinmişse null). Yalnız Project documents ailesindeki dosya
 * bağlanır; kimlik plakası dışındaki türde dosya PDF olmalı; sayfa aralığı yalnız PDF'te ve PDF'in içinde verilir (`sayfaSayisi`
 * bilinmiyorsa üst sınır denetlenmez). `pdf`'i sunucu saklı dosyanın başlık baytlarından verir (ad ya da MIME yetmez: bu aileyi genel
 * yükleme ucu da yazabilir) ve türün içerik kuralını (`girdiIcerikHatasi`) aynı baytlarla ayrıca uygular; web yüklemede denetlediği
 * içerikten verir.
 */
export function girdiDosyasiHatasi(t, d) {
    if (!d)
        return "The selected file is not in OASIS.";
    if (d.aile !== DOSYA_BELGE_AILESI)
        return "Only a file uploaded on Project documents can be attached here.";
    if (!d.pdf && girdiIcerikTurleri(t.tur).every((x) => x === "pdf"))
        return "The document must be a PDF.";
    if (t.sayfaSon !== null) {
        if (!d.pdf)
            return "Pages can be given only for a PDF.";
        if (d.sayfaSayisi && t.sayfaSon > d.sayfaSayisi)
            return `Pages: page ${t.sayfaSon} is beyond this PDF (${d.sayfaSayisi} pages).`;
    }
    return null;
}
/**
 * Yeni bağın etkin bağlarla çakışması (`etkin`: kaldırılmamış satırlar; başka kapsam ve anahtarın satırları yok sayılır). Tekil
 * türde aynı kapsam ve anahtarda etkin belge varken ikincisi eklenmez; aynı tür, dosya ve sayfalar aynı yere iki kez bağlanmaz.
 */
export function girdiCakismaHatasi(yeni, etkin) {
    const ayniYer = etkin.filter((b) => b.kapsam === yeni.kapsam && (b.anahtar ?? null) === (yeni.anahtar ?? null) && b.tur === yeni.tur);
    if (girdiTekilMi(yeni.tur) && ayniYer.length)
        return "Only one document of this type can be attached here; remove the current one first.";
    if (ayniYer.some((b) => b.dokumanId === yeni.dokumanId && (b.sayfaBas ?? null) === (yeni.sayfaBas ?? null) && (b.sayfaSon ?? null) === (yeni.sayfaSon ?? null))) {
        return "This file is already attached here with the same pages.";
    }
    return null;
}
/**
 * Bağ satırını ekran satırına çevirir. Erişim kuralı Riser Documentation ile aynı fonksiyondan (`erisimUygula`): erişimi olmayan
 * rolde satır kalır, belge kimliği gider; dosya adı fiyat taşıyan belgede yalnız belgeyi açabilen role yazılır. `d` dosya kaydıdır
 * (bulunamıyorsa null: satır bağlantısız).
 */
export function dosyaBelgeSatiri(b, d, rol, riserler) {
    const s = erisimUygula(girdiBelgeSatiri(b, d), rol);
    return {
        id: b.id,
        kapsam: b.kapsam,
        anahtar: b.anahtar,
        tur: b.tur,
        dokumanId: d ? s.dokumanId : null,
        baglantisiz: d ? s.baglantisiz : "kimlik_yok",
        dosyaAd: s.dosyaAd,
        pdf: s.pdf,
        sayfaBas: b.sayfaBas,
        sayfaSon: b.sayfaSon,
        not: b.not,
        kim: b.kim,
        ts: b.ts,
        riserSayisi: uygulananRiserSayisi(b, riserler),
    };
}
const turSirasi = (tur) => GIRDI_SEKME_TURLERI[girdiSekmesi(tur)].indexOf(tur);
/**
 * Sekmenin satırları, ekrandaki sırayla. Sekmesi ya da kapsamı uymayan satır alınmaz. Sıra: "Project and work order"da önce iş emri
 * satırları (numara sırasıyla), sonra proje satırları; ardından türün sekmedeki sırası; aynı türde yükleme zamanı.
 */
export function sekmeSatirlari(sekme, satirlar) {
    const turler = GIRDI_SEKME_TURLERI[sekme];
    const kapsamlar = GIRDI_SEKME_KAPSAMLARI[sekme];
    const yer = (s) => (s.kapsam === "proje" ? 1 : 0);
    return satirlar
        .filter((s) => turler.includes(s.tur) && kapsamlar.includes(s.kapsam))
        .sort((a, b) => yer(a) - yer(b) || dogalSira(a.anahtar ?? "", b.anahtar ?? "") || turSirasi(a.tur) - turSirasi(b.tur) || duzSira(a.ts, b.ts) || duzSira(a.id, b.id));
}
/** Riser sekmesinin beş türü, statik sırayla; her türün belgeleri yükleme sırasıyla. Belgesi olmayan tür de listelenir. */
export function riserBelgeGruplari(satirlar) {
    const sirali = sekmeSatirlari("riser", satirlar);
    return GIRDI_SEKME_TURLERI.riser.map((tur) => ({ tur, satirlar: sirali.filter((s) => s.tur === tur) }));
}
/* ====================================================================================================================
 * 5 · Project documents: köken ("Lines and parts from another riser")
 * ================================================================================================================== */
/** Köken satırının parçası: hattın tamamı ya da box, pipe, pin. */
export const KOKEN_PARCALARI = ["hat", ...PARCALAR];
/**
 * Etkin köken haritası: beş hat, SET_HATLARI sırasıyla. Parçanın kökeni kendi satırı, yoksa hattın tamamının satırıdır
 * (izlenebilirlik tablosundaki `hatParcaDurumu` ile aynı kural).
 */
export function kokenSatirlari(kokenler) {
    return SET_HATLARI.map((hat) => {
        const bul = (parca) => kokenler.find((k) => k.hat === hat && k.parca === parca)?.kokenRn ?? null;
        const hatRn = bul("hat");
        return { hat, hatRn, box: bul("box") ?? hatRn, pipe: bul("pipe") ?? hatRn, pin: bul("pin") ?? hatRn };
    });
}
/**
 * Köken seçiminin denetimi ve planı (`rn`: seçimin yapıldığı riser; `etkin`: o riserin etkin köken satırları).
 * - Hat SET_HATLARI'ndan, parça KOKEN_PARCALARI'ndan; verici riser RN biçiminde (OASIS'te olmayan riser yazılabilir).
 * - Riser kendi kendisinin vericisi olamaz.
 * - "Whole line" seçilince o hattın box, pipe ve pin satırları da kaldırılır; hattın tamamı başka riserden gelirken parça seçilemez.
 *   Hattın tamamı "this riser"a dönünce yalnız hat satırı kaldırılır: parça seçimleri yeniden açılır.
 * - Aynı seçim yeniden gönderilirse satır yeniden yazılmaz.
 */
export function kokenYazimi(rn, etkin, ham) {
    const hata = (m) => ({ hata: m, kaldirilacak: [], eklenecek: null });
    if (typeof ham.hat !== "string" || !setHatMi(ham.hat))
        return hata("Unknown line.");
    const hat = ham.hat;
    const parca = KOKEN_PARCALARI.find((p) => p === ham.parca);
    if (!parca)
        return hata("Unknown part.");
    const bos = ham.kokenRn === null || ham.kokenRn === undefined || (typeof ham.kokenRn === "string" && !ham.kokenRn.trim());
    const kokenRn = bos ? null : riserRnOku(ham.kokenRn);
    if (!bos && !kokenRn)
        return hata("Riser number is not valid.");
    if (kokenRn !== null && kokenRn === (riserRnOku(rn) ?? rn))
        return hata("A riser cannot be the donor of its own line or part.");
    const hatSatirlari = etkin.filter((k) => k.hat === hat);
    const tamami = hatSatirlari.find((k) => k.parca === "hat");
    if (parca !== "hat" && tamami)
        return hata(`The whole line comes from riser ${tamami.kokenRn}; set the whole line back to this riser before choosing a part.`);
    const mevcut = hatSatirlari.filter((k) => k.parca === parca);
    const ayni = kokenRn !== null && mevcut.length === 1 && mevcut[0].kokenRn === kokenRn;
    const parcalar = parca === "hat" && kokenRn !== null ? hatSatirlari.filter((k) => k.parca !== "hat") : [];
    return {
        hata: null,
        kaldirilacak: [...(ayni ? [] : mevcut), ...parcalar].map((k) => k.id),
        eklenecek: kokenRn !== null && !ayni ? { hat, parca, kokenRn } : null,
    };
}
/* ====================================================================================================================
 * 5 · Project documents: OEM kaydı sayfaları ("OEM record pages")
 * ================================================================================================================== */
/**
 * OEM kitabında sayfa aralığı tutulan yedi yer, ekrandaki satır sırasıyla: beş hat (bölüm başına değil HAT başına) ve iki DNV
 * belgesi. Elle düzeltme `belge_sayfa`da bu anahtarla durur.
 */
export const OEM_SAYFA_YERLERI = [...SET_HATLARI, "dnv_pc", "oem_dvr"];
export const oemSayfaYeriMi = (y) => typeof y === "string" && OEM_SAYFA_YERLERI.includes(y);
/** Satır adı ("Part of the book"). */
export const OEM_SAYFA_YERI_ADI = { ...DOSYA_HAT_ADI, dnv_pc: DOSYA_TUR_ADI.dnv_urun_sertifikasi, oem_dvr: DOSYA_TUR_ADI.dvr_oem };
/** `belge_sayfa` satırlarından elle aralıklar; tanınmayan yer yok sayılır. */
export function oemElleOku(satirlar) {
    const elle = {};
    for (const s of satirlar)
        if (oemSayfaYeriMi(s.yer))
            elle[s.yer] = { sayfaBas: s.sayfaBas, sayfaSon: s.sayfaSon };
    return elle;
}
export function oemKitapSayfalari(agac, pieceler, elle) {
    return { bolumler: oemBolumSayfalari(agac, pieceler), dnv: dnvSayfalari(pieceler), elle: { ...(elle ?? {}) } };
}
export function oemYerSayfasi(yer, kitap) {
    const hesap = !kitap ? null : yer === "dnv_pc" ? kitap.dnv.urunSertifikasi : yer === "oem_dvr" ? kitap.dnv.tasarimDogrulama : oemHatBolumu(yer, kitap.bolumler).sayfa;
    const elle = kitap?.elle[yer] ?? null;
    return { hesap, elle, sayfa: elle ?? hesap, kaynak: elle ? "elle" : "dizin" };
}
/**
 * Yedi satır, ekrandaki sırayla. `kitap` riserin kendi kitabıdır (MRB kaydı yoksa null). `vericiler`: hattın tamamı başka riserden
 * gelen hatlar için verici riser ve onun kitabı (OASIS'te yoksa ya da kitabı yoksa null). İki DNV belgesi hep riserin kendi kitabındandır.
 */
export function oemSayfaSatirlari(kitap, vericiler = {}) {
    return OEM_SAYFA_YERLERI.map((yer) => {
        const verici = setHatMi(yer) ? vericiler[yer] : undefined;
        return { yer, ...oemYerSayfasi(yer, verici ? verici.kitap : kitap), kokenRn: verici?.rn ?? null };
    });
}
/**
 * OEM sayfa düzeltmesinin denetimi (`hatRn`: düzeltilen hattın tamamı başka riserden geliyorsa o riser). Aralığın biçimi ve PDF'in
 * sayfa sayısına uyması sunucuda var olan sayfa aralığı denetimiyle yapılır.
 */
export function oemSayfaHatasi(yer, hatRn) {
    if (!oemSayfaYeriMi(yer))
        return "Unknown part of the OEM record book.";
    return hatRn && setHatMi(yer) ? `This line comes from riser ${hatRn}; correct the pages on the page of that riser.` : null;
}
/**
 * Satır bağ alan canlı bir rapor mu: 3.5'e giren basınç testi sertifikası ya da NDT raporu (Finishing hat NDT'si, işin test adımı
 * raporu, post-test raporu). İptal edilmiş işin raporu değildir. Riser Documentation'da 5.5 ve 5.4'ün yuvaları da aynı satırların
 * belge kimliği olanlarından doğar (`canliRaporTipi`).
 */
export function raporTipi(s) {
    if (s.iptalIs)
        return null;
    if (s.kalem === "3.5" && s.tur === "basinc_testi")
        return "basinc_testi";
    return NDT_RAPOR_TURLERI.includes(s.tur) ? "ndt" : null;
}
/**
 * Satırın belgesinin kimliği var mı. Role bağlı değildir: erişimi olmayan rolde kimlik gizlenir (`baglantisiz: "erisim"`) ama belge
 * vardır; kayıtta belge kimliği yoksa (`docs[adım] === true`, bulunamayan belge) yoktur.
 */
export function belgeKimligiVar(s) {
    return s.dokumanId !== null || s.baglantisiz === "erisim";
}
/**
 * Canlı raporun tipi: bağ alabilen rapor (`raporTipi`) ve belge kimliği olan satır; değilse null. Bağ belgede durduğu için kimliği
 * olmayan kayda imzalayan ya da ekipman bağlanamaz: "Reports — test equipment and signer" tablosu ve 5.4 / 5.5'in yuvaları aynı
 * ölçütle bu satırlardan doğar.
 */
export function canliRaporTipi(s) {
    return belgeKimligiVar(s) ? raporTipi(s) : null;
}
/** Dosyada Bölüm 1–5'te çizilen satırlar, çizim sırasıyla (Reference hariç); sayısı `belgeSayisi`dir. */
export function cizilenSatirlar(d) {
    return d.bolumler.flatMap((b) => [...b.gruplar.flatMap((g) => g.satirlar), ...b.bloklar.flatMap((x) => x.satirlar), ...b.kutukSatirlari]);
}
/**
 * Riserin canlı raporları ve seçili bağları. `satirlar` riserin Riser Documentation satırlarıdır (`cizilenSatirlar`: 3.5'e giren
 * sertifikalar ve 3.2 / 4.2 / 4.5'e giren NDT raporları oradaki adlarıyla; küme ikinci kez hesaplanmaz). Belge kimliği olmayan rapor
 * listelenmez (bağ belgede durur); aynı belge bir kez. Sıra: önce basınç testi sertifikaları (Choke, Kill, Booster, Hydraulic),
 * sonra NDT raporları geliş sırasıyla.
 */
export function raporSatirlari(satirlar, izBaglari) {
    const bag = new Map(izBaglari.map((b) => [b.dokumanId, b]));
    const gorulen = new Set();
    const sonuc = [];
    for (const s of satirlar) {
        const tip = canliRaporTipi(s);
        if (!tip || !s.dokumanId || gorulen.has(`${tip}|${s.dokumanId}`))
            continue;
        gorulen.add(`${tip}|${s.dokumanId}`);
        const b = bag.get(s.dokumanId);
        sonuc.push({ dokumanId: s.dokumanId, tip, satir: s, aletler: tip === "basinc_testi" ? [...(b?.aletler ?? [])] : [], imzalayanlar: tip === "ndt" ? [...(b?.imzalayanlar ?? [])] : [] });
    }
    const hatSirasi = (r) => {
        const i = BASINC_TESTI_HATLARI.indexOf(r.satir.hat ?? "");
        return i < 0 ? BASINC_TESTI_HATLARI.length : i;
    };
    const sira = (r) => (r.tip === "basinc_testi" ? hatSirasi(r) : BASINC_TESTI_HATLARI.length + 1);
    return sonuc.map((r, i) => ({ r, i })).sort((a, b) => sira(a.r) - sira(b.r) || a.i - b.i).map((x) => x.r);
}
const metinListesi = (v) => [...new Set((Array.isArray(v) ? v : []).filter((x) => typeof x === "string").map((x) => x.trim()).filter(Boolean))];
const izNesnesi = (ham) => (ham && typeof ham === "object" && !Array.isArray(ham) ? ham : {});
/**
 * `iz_baglari` kolonunu toleranslı okur: dizi olmayan alan boş sayılır; metin olmayan, boş ve yinelenen öğe atılır. Eski kodlu
 * `ndtPersonel` anahtarı OKUNMAZ (D-062): yalnız onu taşıyan belgenin imzalayanı yok sayılır.
 */
export function izBaglariOku(ham) {
    const n = izNesnesi(ham);
    return { aletler: metinListesi(n.aletler), ndtInspektor: metinListesi(n.ndtInspektor) };
}
/**
 * Yazılacak kolon değeri: yalnız dolu alanlar. `onceki` kolonun yazımdan önceki ham değeridir (yeni belgede null): içindeki eski
 * kodlu `ndtPersonel` listesi aynen taşınır (bu fonksiyon JSON'u baştan kurar; taşınmasaydı tanınmayan anahtar düşerdi). Alet de
 * imzalayan da eski kod da yoksa null (kolon boşaltılır).
 */
export function izBaglariKaydi(b, kim, ts, onceki) {
    const eski = izNesnesi(onceki).ndtPersonel;
    const ndtPersonel = Array.isArray(eski) ? eski.filter((x) => typeof x === "string") : [];
    if (!b.aletler.length && !b.ndtInspektor.length && !ndtPersonel.length)
        return null;
    return {
        ...(b.aletler.length ? { aletler: [...b.aletler] } : {}),
        ...(b.ndtInspektor.length ? { ndtInspektor: [...b.ndtInspektor] } : {}),
        ...(ndtPersonel.length ? { ndtPersonel } : {}),
        kim,
        ts,
    };
}
const IMZALAYAN_SECILMEDI_HATASI = "Select the NDT inspector who signed the report, or confirm that the signer will be entered later.";
const INSPEKTOR_KUTUKTE_YOK_HATASI = "The selected NDT inspector is not in the Registers; refresh the page.";
const IMZALAYAN_LISTESI_HATASI = "The signer list is not valid.";
const inspektorPasifHatasi = (ad) => `NDT inspector ${ad} is inactive in the Registers.`;
/** İmzalayan listesi yalnız dolu metinlerden (inspektör kimlikleri) oluşur. */
const kimlikListesiMi = (v) => Array.isArray(v) && v.every((x) => typeof x === "string" && x.trim() !== "");
/**
 * Ekipman / imzalayan bağının denetimi ve yeni değeri.
 * - `tip`: belgenin riserin canlı rapor kümesindeki tipi (`raporTipi`); kümede değilse null: bağ yazılmaz.
 * - Test ekipmanı yalnız basınç testi sertifikasına, imzalayan yalnız NDT raporuna bağlanır.
 * - Alet kodu alet kütüğünde olmalı; pasif alet yeni seçilemez, zaten seçili pasif alet kalabilir.
 * - D-062: imzalayan inspektörün KİMLİĞİYLE verilir (`govde.ndtInspektor`) ve inspektör kütüğünde olmalıdır; pasif inspektör yeni
 *   seçilemez, zaten seçili pasif inspektör kalabilir. Önceden seçili olup kütükte artık olmayan kimlik hata vermez, listeden
 *   sessizce düşer. Kod biçimi kuralı ve "belgesi olmayan kod da bağlanır" kuralı kalktı; eski `ndtPersonel` alanı okunmaz.
 */
export function izBagiHazirla(g) {
    const onceki = izBaglariOku(g.onceki);
    const hata = (m) => ({ ...onceki, degisti: false, hata: m });
    if (!g.tip)
        return hata("Test equipment and signer can be set only on a pressure test certificate or an NDT report that is on file.");
    const { aletler: hamAletler, ndtInspektor: hamInspektor } = g.govde;
    const verildi = (v) => Array.isArray(v);
    if ((hamAletler === undefined && hamInspektor === undefined) || (hamAletler !== undefined && !verildi(hamAletler)) || (hamInspektor !== undefined && !verildi(hamInspektor))) {
        return hata("Give the test equipment or the signer.");
    }
    if (hamAletler !== undefined && g.tip !== "basinc_testi")
        return hata("Test equipment can be set only on a pressure test certificate.");
    if (hamInspektor !== undefined && g.tip !== "ndt")
        return hata("A signer can be set only on an NDT report.");
    let aletler = onceki.aletler;
    if (hamAletler !== undefined) {
        aletler = metinListesi(hamAletler);
        for (const kod of aletler) {
            const a = g.aletKutugu.find((x) => x.kod === kod);
            if (!a)
                return hata(`Instrument ${kod} is not in the Instruments register.`);
            if (!a.aktif && !onceki.aletler.includes(kod))
                return hata(`Instrument ${kod} is inactive in the Registers.`);
        }
    }
    let ndtInspektor = onceki.ndtInspektor;
    if (hamInspektor !== undefined) {
        if (!kimlikListesiMi(hamInspektor))
            return hata(IMZALAYAN_LISTESI_HATASI);
        ndtInspektor = [];
        for (const id of metinListesi(hamInspektor)) {
            const k = g.inspektorKutugu.find((x) => x.id === id);
            const zatenSecili = onceki.ndtInspektor.includes(id);
            if (!k) {
                // Kütükten silinmiş eski seçim: gösterilmiyordu, yazımda da düşer.
                if (zatenSecili)
                    continue;
                return hata(INSPEKTOR_KUTUKTE_YOK_HATASI);
            }
            if (!k.aktif && !zatenSecili)
                return hata(inspektorPasifHatasi(k.ad));
            ndtInspektor.push(id);
        }
    }
    const ayni = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
    return { aletler, ndtInspektor, degisti: !ayni(aletler, onceki.aletler) || !ayni(ndtInspektor, onceki.ndtInspektor), hata: null };
}
/* ====================================================================================================================
 * 5 · NDT inspektörü ve imzalayan (D-062): ad kuralları, seçim listesi, yüklemede "imzalayan ya da onay"
 * ================================================================================================================== */
/** İnspektörün adını yazılmadan önce düzeltir: baş ve son boşluk atılır, iç boşluklar tek boşluk olur. Metin değilse boş. */
export function inspektorAdiDuzelt(ham) {
    return typeof ham === "string" ? ham.trim().replace(/\s+/g, " ") : "";
}
/**
 * Adın karşılaştırma anahtarı: aynı ad ikinci kez açılmasın diye harf büyüklüğü, Türkçe İ / ı ve aksan farkı gözetilmez
 * ("ÇAĞRI  Işık" ile "cagri isik" aynı anahtardır). Sıra: düzeltme → NFKD → aksan işaretleri atılır → ı → i → küçük harf → tek
 * boşluk. Anahtar yalnız karşılaştırmada kullanılır; saklanmaz, ekrana yazılmaz. Gerçekten iki ayrı kişi aynı adı taşıyorsa ada
 * ayırt edici bir ek yazılır.
 */
export function inspektorAdAnahtari(ad) {
    return inspektorAdiDuzelt(ad).normalize("NFKD").replace(/\p{M}+/gu, "").replace(/ı/g, "i").toLowerCase().replace(/\s+/g, " ").trim();
}
/**
 * Yeni seçim listesi ("+ Add"): kütükteki AKTİF ve henüz seçili olmayan inspektörler, ad sırasıyla (sıra `inspektorAdAnahtari`
 * üzerinden: harf büyüklüğü ve aksan sırayı bozmaz). Pasif inspektör yeni seçimde sunulmaz.
 */
export function inspektorSecimleri(liste, secili) {
    return liste
        .filter((k) => k.aktif && !secili.includes(k.id))
        .map((k) => ({ k, a: inspektorAdAnahtari(k.ad) }))
        .sort((x, y) => duzSira(x.a, y.a) || duzSira(x.k.ad, y.k.ad) || duzSira(x.k.id, y.k.id))
        .map((x) => x.k);
}
/**
 * Bu yüklemede imzalayan sorulur mu: yalnız RİSER grubundaki işin NDT rapor adımında. NDT adımı adım ADINDAN tanınır (ölçüt Riser
 * Documentation'ın NDT raporu ölçütüyle aynı: `adimTuru` + `NDT_RAPOR_TURLERI`); PWHT ve hidrotest adımı, düz adım ve klamp işi
 * (`grup` "clamp") sorulmaz. Rota şablonuna bu kalıba uymayan yeni bir NDT adım adı yazılırsa o adımda imzalayan sorulmaz.
 * Finishing'in hat NDT raporu adım değildir: orada imzalayan her yüklemede sorulur (riser dışında hat NDT'si yoktur).
 */
export function imzalayanSorulurMu(adimAd, grup) {
    return grup === "riser" && NDT_RAPOR_TURLERI.includes(adimTuru(adimAd ?? ""));
}
/**
 * NDT raporu yükleme isteğinin iki isteğe bağlı alanını okur (form-data metni): `imzalayanlar` = inspektör kimliklerinin JSON
 * dizisi ('["…","…"]'; dizi olarak da verilebilir), `imzalayanOnay` = "true" (imzalayan seçilmeden yüklemenin onayı).
 * - Alan yoksa ya da boşsa imzalayan yok sayılır; yinelenen kimlik bir kez alınır.
 * - JSON bozuksa, dizi değilse ya da öğelerden biri dolu metin değilse hata: "The signer list is not valid.".
 * - İkisi birden gelirse imzalayanlar geçerlidir: `onay` yalnız imzalayan seçilmemişken true döner.
 * Kütük denetimi ayrıca yapılır (`yuklemeImzalayanHatasi`).
 */
export function imzalayanGovdesiOku(ham) {
    const bozuk = { imzalayanlar: [], onay: false, hata: IMZALAYAN_LISTESI_HATASI };
    let liste = ham?.imzalayanlar ?? [];
    if (typeof liste === "string") {
        const metin = liste.trim();
        if (!metin)
            liste = [];
        else {
            try {
                liste = JSON.parse(metin);
            }
            catch {
                return bozuk;
            }
        }
    }
    if (!kimlikListesiMi(liste))
        return bozuk;
    const imzalayanlar = metinListesi(liste);
    const onayHam = ham?.imzalayanOnay;
    const onay = onayHam === true || (typeof onayHam === "string" && onayHam.trim().toLowerCase() === "true");
    return { imzalayanlar, onay: onay && imzalayanlar.length === 0, hata: null };
}
/**
 * Yüklemede "imzalayan ya da onay" kuralı (ekranda ve sunucuda aynı fonksiyon; sunucu dosyayı yazmadan önce ölçer). `govde`
 * `imzalayanGovdesiOku`'nun sonucudur; `kutuk` Registers › NDT inspectors'ın tamamıdır (pasifler dahil).
 * - İmzalayan da onay da yok: "Select the NDT inspector who signed the report, or confirm that the signer will be entered later."
 * - Seçilen kimlik kütükte yok: "The selected NDT inspector is not in the Registers; refresh the page."
 * - Seçilen inspektör pasif: "NDT inspector <ad> is inactive in the Registers." (yüklemede pasif inspektör hiç seçilemez).
 */
export function yuklemeImzalayanHatasi(govde, kutuk) {
    if (!govde.imzalayanlar.length)
        return govde.onay ? null : IMZALAYAN_SECILMEDI_HATASI;
    for (const id of govde.imzalayanlar) {
        const k = kutuk.find((x) => x.id === id);
        if (!k)
            return INSPEKTOR_KUTUKTE_YOK_HATASI;
        if (!k.aktif)
            return inspektorPasifHatasi(k.ad);
    }
    return null;
}
/**
 * Belgenin imzalayan listesine yüklemede seçilenleri EKLER: tekil, sıra korunur (önce var olanlar). Yükleme hiçbir imzalayanı
 * silmez: aynı PDF aynı riser + hat + adım adına yeniden gelince tek belge satırı paylaşılır. Silme yalnız Project documents'tan
 * (liste öncekinin yerine geçer: `izBagiHazirla`) ve Component'te PDF adımdan kaldırılınca olur.
 */
export function imzalayanBirlestir(onceki, secilen) {
    return [...new Set([...onceki, ...secilen])];
}
//# sourceMappingURL=riser-dosyasi.js.map